import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connector, vuser } from '../services/api';
import './DatasetsPage.css';
import '../components/ui/ModalFormField.css';
import ModalFormField from './ui/ModalFormField';
import { useModalFormValidation } from '../hooks/useModalFormValidation';
import {
  MIN_SPLIT_PCT,
  nextSplitRatios,
  EXPORT_DATASET_NAME_ID,
  EXPORT_ERROR_BANNER_ID,
  EXPORT_FILTERS_ID,
  EXPORT_DIALOGS_SECTION_ID,
  EXPORT_PERIOD_SECTION_ID,
  EXPORT_USERS_SECTION_ID,
  EXPORT_REACTION_WEIGHTS_PREFIX,
  START_EXP_DATASET_NAME_ID,
  START_EXP_DIALOG_COUNT_ID,
  START_EXP_ERROR_BANNER_ID,
  START_EXP_FIRST_MESSAGE_ID,
  START_EXP_MESSAGES_PER_DIALOG_ID,
  START_EXP_REACTION_WEIGHTS_PREFIX,
  START_EXP_ADMIN_PERSONA_PAIRS_ID,
  START_EXP_USER_PERSONA_PAIRS_ID,
  START_EXP_VIRTUAL_USERS_ID,
  validateExportForm,
  validateStartExperimentForm,
} from '../utils/datasetModalValidation';
import { stripFieldIdPrefix } from '../utils/modalFormValidation';
import type {
  ConnectorDataset,
  DatasetContentResponse,
  DatasetDataWarning,
  DatasetExportPreviewData,
  DatasetExportRequest,
  DatasetImportJob,
  DatasetSourceDialog,
  DatasetSourceUser,
} from '../types/api';
import type { DatasetGenerationPredictTokenUsageResponse } from '../types/datasetGenerationPreview';
import type { Experiment, Dialog, ExperimentScoreBreakdown, ExperimentTokenStatistics, VirtualUser, WorkflowStatesSnapshot } from '../types/vuser';
import ExperimentScoreModal from './admin/ExperimentScoreModal';
import ConnectorSelectIntegrationPrompt from './connector/ConnectorSelectIntegrationPrompt';
import MultiSelect, { type MultiSelectOption } from './admin/MultiSelect';
import TruncatedText from './admin/TruncatedText';
import DatasetViewerTurnList from './dataset/DatasetViewerTurnList';
import DateTimePicker from './ui/DateTimePicker';
import HistoryContextControl from './ui/HistoryContextControl';
import {
  clampHistoryDepth,
  commitPositiveIntegerDraft,
  numberInputDisplay,
  sanitizePositiveIntegerDraft,
} from '../utils/numberInputDraft';
import {
  DATASET_GENERATION_MAX_DIALOGS,
  DATASET_GENERATION_MAX_MESSAGES_PER_DIALOG,
  DATASET_GENERATION_MIN_DIALOGS,
  DATASET_GENERATION_MIN_MESSAGES_PER_DIALOG,
} from '../constants/datasetGenerationLimits';
import FineTuningReactionWeightsFields from './FineTuningReactionWeightsFields';
import ProgressBar from './ui/ProgressBar';
import DatasetImportJobProgress from './dataset/DatasetImportJobProgress';
import { useJobsApiProgressDisplay } from '../hooks/useJobsApiProgressDisplay';
import ExperimentPersonaPairsEditor, {
  rowsToBindings,
  type ExperimentPersonaPairRow,
} from './ExperimentPersonaPairsEditor';
import './ExperimentPersonaPairsEditor.css';
import { getExperimentProgressMetrics } from '../utils/progressBar';
import { resolveExperimentDisplayState } from '../utils/experimentDisplay';
import ExperimentStatusBadge from './dataset/ExperimentStatusBadge';
import './dataset/ExperimentStatusBadge.css';
import {
  formatDatasetFieldValue,
  formatDisplayNumber,
  formatReactionWeightValue,
  isWeightFieldKey,
} from '../utils/formatDisplayNumber';
import {
  buildReactionWeightsFromForm,
  DEFAULT_REACTION_WEIGHTS_FORM,
  EMPTY_REACTION_WEIGHTS_FORM,
  type ReactionWeightsFormState,
} from '../types/reactionWeights';
import {
  buildDatasetImportSuccessMessage,
  clearPersistedDatasetImportJobId,
  formatDatasetImportJobPhase,
  isDatasetImportJobActive,
  isDatasetImportJobTerminal,
  persistDatasetImportJobId,
  readPersistedDatasetImportJobId,
  resolveDatasetImportJobProgressPercent,
  shouldUpdateDatasetImportJob,
} from '../utils/datasetImportJob';
import { capitalizeError, extractHttpErrorBodyMessage, formatDatasetImportErrorMessage, formatDatasetImportJobErrorMessage, getErrorMessage, resolveDatasetImportApiError } from '../utils/errorUtils';
import {
  DATASET_IMPORT_MODAL_SUCCESS_AUTO_CLOSE_MS,
  NOTIFICATION_AUTO_HIDE_MS,
} from '../constants/uiTiming';
import { canRetryExperiment } from '../utils/trainingJobRetry';
import { getDefaultTrainingDateRange } from '../utils/defaultTrainingDateRange';
import DatasetGenerationTokenPreviewView from './dataset/DatasetGenerationTokenPreviewView';
import './dataset/DatasetGenerationTokenPreviewView.css';
import {
  buildAssistantConfigPayloadFromIntegration,
  buildDatasetGenerationPredictTokenUsageRequest,
  buildVuserConfigPayload,
} from '../utils/datasetGenerationPreviewRequest';

const DATASET_IMPORT_POLL_INTERVAL_MS = 2000;
const pulseStyle = document.createElement('style');
pulseStyle.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
if (!document.head.querySelector('style[data-pulse-animation]')) {
  pulseStyle.setAttribute('data-pulse-animation', 'true');
  document.head.appendChild(pulseStyle);
}

// Constants for experiment limits
const MAX_EXPERIMENT_DIALOGS = DATASET_GENERATION_MAX_DIALOGS;
const MAX_MESSAGES_PER_DIALOG = DATASET_GENERATION_MAX_MESSAGES_PER_DIALOG;
const DEFAULT_HISTORY_DEPTH = 10;

const DEFAULT_EXPORT_SIGNAL_FILTERS = {
  reactions: ['like', 'dislike'] as ('like' | 'dislike')[],
  comment: true,
  groundtruth: true,
};

type DatasetContentTableCacheEntry = {
  loaded: boolean;
  stats: Record<string, unknown> | null;
  warning: DatasetDataWarning | null;
};

function exportFormHasAtLeastOneSignal(form: {
  reactions: ('like' | 'dislike')[];
  comment: boolean;
  groundtruth: boolean;
}): boolean {
  return form.reactions.length > 0 || form.comment || form.groundtruth;
}
const EXPORT_REACTION_WEIGHTS_ID_PREFIX = EXPORT_REACTION_WEIGHTS_PREFIX;

/** Compact action buttons in dataset tables */
const DATASET_ACTION_BTN_BASE: React.CSSProperties = {
  minWidth: 0,
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  border: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  boxSizing: 'border-box',
  transition: 'background 0.15s ease, box-shadow 0.15s ease',
  whiteSpace: 'nowrap',
};

function formatVirtualUserCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Columns not shown in the connector datasets table (redundant or internal). */
const HIDDEN_CONNECTOR_DATASET_COLUMNS = new Set([
  'integration_id',
  'integrationId',
  'Integration ID',
  'integration id',
  'stats',
  'warning',
  /** Shown formatted in dataset viewer, not as a JSON column. */
  'metadata',
]);

const METADATA_PANEL_SKIP_KEYS = new Set(['stats', 'training_period', 'filters']);

const INTEGRATION_METADATA_KEYS = [
  'training_period',
  'filters',
  'train_ratio',
  'validation_ratio',
  'test_ratio',
  'limit_history',
  'history_depth',
] as const;

const METADATA_KEY_ORDER = [
  'origin',
  'source',
  'experiment_id',
  'inner_label',
  'integration_id',
  'virtual_user_ids',
  'virtual_admin_ids',
  'dialog_count',
  'messages_per_dialog',
  'train_ratio',
  'validation_ratio',
  'test_ratio',
  'limit_history',
  'history_depth',
];

const DATASET_FIELD_LABELS: Record<string, string> = {
  id: 'ID',
  label: 'Label',
  name: 'Name',
  source: 'Source',
  storage: 'Storage',
  created_at: 'Created at',
  updated_at: 'Updated at',
  inner_label: 'Inner label',
  s3_key: 'S3 key',
  integration_id: 'Integration ID',
  experiment_id: 'Experiment ID',
  train_ratio: 'Train ratio',
  validation_ratio: 'Validation ratio',
  test_ratio: 'Test ratio',
  limit_history: 'Limit history',
  history_depth: 'History depth',
  dialog_count: 'Dialog count',
  messages_per_dialog: 'Messages per dialog',
  virtual_user_ids: 'Virtual user IDs',
  virtual_admin_ids: 'Virtual admin IDs',
  train_rows: 'Train rows',
  validation_rows: 'Validation rows',
  test_rows: 'Test rows',
  total_rows: 'Total rows',
  visible_dialogs: 'Visible dialogs',
  invisible_dialogs: 'Hidden dialogs (privacy)',
  dialogs: 'Dialogs',
  messages: 'Messages',
  likes: 'Likes',
  dislikes: 'Dislikes',
  comments: 'Comments',
  groundtruth: 'Ground truth',
  dataset_tokens_original: 'Dataset tokens (original)',
  training_period: 'Training period',
  filters: 'Filters',
  origin: 'Origin',
  reaction_weight: 'Reaction weight',
  __stats_summary: 'Stats',
  __warning: 'Tuning readiness',
};

const DATASET_STATS_KEYS = [
  'dialogs',
  'messages',
  'likes',
  'dislikes',
  'comments',
  'groundtruth',
  'train_rows',
  'validation_rows',
  'test_rows',
  'total_rows',
  'visible_dialogs',
  'invisible_dialogs',
] as const;

/** Table headers and metadata labels — no raw snake_case in the UI. */
function formatDatasetFieldLabel(key: string): string {
  const trimmed = key.trim();
  if (!trimmed) return key;
  if (DATASET_FIELD_LABELS[trimmed]) return DATASET_FIELD_LABELS[trimmed];
  return trimmed
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function readFiniteDatasetNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function getNestedDatasetObject(value: unknown, key: string): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const nested = (value as Record<string, unknown>)[key];
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return null;
  return nested as Record<string, unknown>;
}

function getDatasetStatsFromRow(row: ConnectorDataset): Record<string, unknown> | null {
  const direct = getNestedDatasetObject(row, 'stats');
  if (direct) return direct;
  const metadata = parseDatasetMetadata((row as Record<string, unknown>).metadata);
  if (metadata?.stats && typeof metadata.stats === 'object' && !Array.isArray(metadata.stats)) {
    return metadata.stats as Record<string, unknown>;
  }
  const picked: Record<string, unknown> = {};
  DATASET_STATS_KEYS.forEach((key) => {
    const value = (row as Record<string, unknown>)[key];
    if (value !== undefined && value !== null) picked[key] = value;
  });
  return Object.keys(picked).length > 0 ? picked : null;
}

function getDatasetWarningFromRow(row: ConnectorDataset): DatasetDataWarning | null {
  const direct = (row as Record<string, unknown>).warning;
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct as DatasetDataWarning;
  }
  const metadata = parseDatasetMetadata((row as Record<string, unknown>).metadata);
  const warning = metadata?.warning;
  if (warning && typeof warning === 'object' && !Array.isArray(warning)) {
    return warning as DatasetDataWarning;
  }
  return null;
}

function formatDatasetStatsSummary(stats: Record<string, unknown> | null): string {
  if (!stats) return '—';
  const visibleDialogs = readFiniteDatasetNumber(stats.visible_dialogs);
  const invisibleDialogs = readFiniteDatasetNumber(stats.invisible_dialogs);
  const dialogs =
    readFiniteDatasetNumber(stats.dialogs) ??
    (visibleDialogs !== null || invisibleDialogs !== null ? (visibleDialogs ?? 0) + (invisibleDialogs ?? 0) : null);
  const messages = readFiniteDatasetNumber(stats.messages ?? stats.total_rows);
  const signals = [
    ['Likes', readFiniteDatasetNumber(stats.likes)],
    ['Dislikes', readFiniteDatasetNumber(stats.dislikes)],
    ['Comments', readFiniteDatasetNumber(stats.comments)],
    ['Ground truth', readFiniteDatasetNumber(stats.groundtruth)],
  ]
    .filter(([, value]) => value !== null)
    .map(([label, value]) => `${label} ${Number(value).toLocaleString()}`);

  const main = [
    dialogs !== null ? `${dialogs.toLocaleString()} dialogs` : null,
    messages !== null ? `${messages.toLocaleString()} messages` : null,
  ].filter(Boolean);
  const parts = [...main, ...signals];
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function formatDatasetWarningRequirement(warning: DatasetDataWarning): string | null {
  const parts = [
    warning.required_total != null ? `total ${warning.required_total}` : null,
    warning.required_train != null ? `train ${warning.required_train}` : null,
    warning.required_validation != null ? `validation ${warning.required_validation}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? `Required: ${parts.join(', ')}` : null;
}

function DatasetWarningBanner({
  warning,
  compact = false,
}: {
  warning?: DatasetDataWarning | null;
  compact?: boolean;
}) {
  if (!warning) return null;
  const requirement = formatDatasetWarningRequirement(warning);
  return (
    <div className={`dataset-warning-banner${compact ? ' dataset-warning-banner--compact' : ''}`} role="status">
      <i className="fas fa-exclamation-triangle" aria-hidden />
      <div>
        <strong>{warning.message || 'Dataset was created, but may be too small for tuning.'}</strong>
        {requirement ? <span>{requirement}</span> : null}
      </div>
    </div>
  );
}

/** API may return metadata as object or JSON string. */
function parseDatasetMetadata(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return null;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

/**
 * Трёхстороннее деление train/validation/test для формы импорта датасета
 * (FE-2). Отдельный компонент: `renderRatioPercent` в
 * `PipelineTaskConfigEditor.tsx` общий с пайплайнами, его трогать нельзя.
 *
 * Train и test двигаются каждый своим слайдером, validation — остаток: сумма
 * всегда 100. Слайдер сам не даёт train и test пересечься за границу 100%.
 */
function TrainValTestRatioSlider({
  train,
  validation,
  test,
  onChange,
  disabled,
  idPrefix,
}: {
  train: number;
  validation: number;
  test: number;
  onChange: (next: { train: number; validation: number; test: number }) => void;
  disabled?: boolean;
  idPrefix: string;
}) {
  const summary = `${train}% train, ${validation}% validation, ${test}% test`;
  const trainMax = 100 - MIN_SPLIT_PCT - test;
  const testMax = 100 - MIN_SPLIT_PCT * 2;

  const current = { train, validation, test };
  const handleTrainChange = (value: number) => onChange(nextSplitRatios(current, { train: value }));
  const handleTestChange = (value: number) => onChange(nextSplitRatios(current, { test: value }));

  return (
    <div className="train-val-ratio" style={{ '--train-pct': `${train}%` } as React.CSSProperties}>
      <div className="train-val-ratio__stats" role="group" aria-label="Train, validation and test split">
        <div className="train-val-ratio__stat train-val-ratio__stat--train">
          <span className="train-val-ratio__stat-label">Train</span>
          <span className="train-val-ratio__stat-value">{train}%</span>
        </div>
        <div className="train-val-ratio__stat train-val-ratio__stat--val">
          <span className="train-val-ratio__stat-label">Validation</span>
          <span className="train-val-ratio__stat-value">{validation}%</span>
        </div>
        <div className="train-val-ratio__stat train-val-ratio__stat--test">
          <span className="train-val-ratio__stat-label">Test</span>
          <span className="train-val-ratio__stat-value">{test}%</span>
        </div>
      </div>
      <div className="train-val-ratio__slider-shell">
        <label className="train-val-ratio__stat-label" htmlFor={`${idPrefix}-train-range`}>Train</label>
        <input
          id={`${idPrefix}-train-range`}
          type="range"
          className="train-val-ratio__range"
          min={MIN_SPLIT_PCT}
          max={trainMax}
          step={1}
          value={train}
          onChange={(e) => handleTrainChange(Number(e.target.value))}
          disabled={disabled}
          aria-valuemin={MIN_SPLIT_PCT}
          aria-valuemax={trainMax}
          aria-valuenow={train}
          aria-valuetext={summary}
          aria-label="Adjust train percentage"
        />
      </div>
      <div className="train-val-ratio__slider-shell">
        <label className="train-val-ratio__stat-label" htmlFor={`${idPrefix}-test-range`}>Test</label>
        <input
          id={`${idPrefix}-test-range`}
          type="range"
          className="train-val-ratio__range"
          min={0}
          max={testMax}
          step={1}
          value={test}
          onChange={(e) => handleTestChange(Number(e.target.value))}
          disabled={disabled}
          aria-valuemin={0}
          aria-valuemax={testMax}
          aria-valuenow={test}
          aria-valuetext={summary}
          aria-label="Adjust test percentage"
        />
      </div>
      <p className="train-val-ratio__hint">
        Train and test move independently; validation is what is left over — all three always add up to 100%,
        and train and validation never drop below {MIN_SPLIT_PCT}%.
      </p>
    </div>
  );
}

const UNTITLED_DIALOG_LABEL = 'Untitled dialog';

function ExportSourceDialogTitle({ title }: { title?: string | null }) {
  const trimmed = (title ?? '').trim();
  if (trimmed) {
    return <div className="export-dataset-dialog-title">{trimmed}</div>;
  }
  return (
    <div className="export-dataset-dialog-title export-dataset-dialog-title--fallback" aria-label={UNTITLED_DIALOG_LABEL}>
      {UNTITLED_DIALOG_LABEL}
    </div>
  );
}

function formatExportUserSelectionSummary(count: number): string {
  if (count === 0) {
    return 'No users selected — pick at least one to load dialogs.';
  }
  if (count === 1) {
    return '1 user selected — only their dialogs appear in the list below.';
  }
  return `${count} users selected — only their dialogs appear in the list below.`;
}

function formatExportDialogSelectionSummary(count: number, visibleOnPage: number): string {
  if (count === 0) {
    return 'No dialogs selected — choose at least one for import.';
  }
  const base = count === 1 ? '1 dialog selected for import' : `${count} dialogs selected for import`;
  if (visibleOnPage > 0) {
    return `${base} (${visibleOnPage} visible on this page)`;
  }
  return base;
}

function formatMetadataCellSummary(v: unknown): string {
  const meta = parseDatasetMetadata(v);
  if (!meta) {
    if (typeof v === 'string' && v.trim().startsWith('{')) return 'View for details';
    return '—';
  }
  const parts: string[] = [];
  if (meta.origin) parts.push(formatDatasetSourceLabel(meta.origin));
  else if (meta.source) parts.push(formatDatasetSourceLabel(meta.source));
  const st = meta.stats;
  if (st && typeof st === 'object' && (st as Record<string, unknown>).total_rows != null) {
    parts.push(`${(st as Record<string, unknown>).total_rows} rows`);
  }
  if (meta.dialog_count != null) parts.push(`${meta.dialog_count} dialogs`);
  return parts.length > 0 ? parts.join(' · ') : 'View for details';
}

function formatDatasetSourceLabel(value: unknown): string {
  if (value === null || value === undefined) return '—';
  const raw = String(value).trim();
  if (!raw) return '—';

  const normalized = raw.toLowerCase();
  const knownLabels: Record<string, string> = {
    integration: 'Integration',
    vuser: 'Virtual User',
    vuser_experiment: 'Virtual User experiment',
    virtual_user_experiment: 'Virtual User experiment',
    export: 'Export',
    import: 'Import',
  };
  if (knownLabels[normalized]) return knownLabels[normalized];

  return raw
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function formatConnectorCell(v: unknown, columnKey?: string): string {
  if (columnKey === 'metadata') return formatMetadataCellSummary(v);
  if (columnKey === 'source') return formatDatasetSourceLabel(v);
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') {
    const trimmed = v.trim();
    const parsed = Date.parse(trimmed);
    // Render ISO-like timestamps as local datetime in tables.
    if (!Number.isNaN(parsed) && (trimmed.includes('T') || trimmed.endsWith('Z'))) {
      return new Date(parsed).toLocaleString();
    }
    if (columnKey && isWeightFieldKey(columnKey)) {
      return formatReactionWeightValue(v);
    }
    return v;
  }
  if (typeof v === 'number') {
    if (columnKey && isWeightFieldKey(columnKey)) {
      return formatReactionWeightValue(v);
    }
    return formatDisplayNumber(v);
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function formatMetadataFieldValue(key: string, value: unknown): React.ReactNode | null {
  if (value === null || value === undefined) return '—';
  if (key === 'source') return formatDatasetSourceLabel(value);
  if (key === 'train_ratio' || key === 'validation_ratio' || key === 'test_ratio') return formatDatasetRatio(value);
  if (key === 'virtual_user_ids' || key === 'virtual_admin_ids') return formatMetadataIdList(value);
  if (key === 'training_period' && typeof value === 'object') {
    const period = value as Record<string, unknown>;
    return `${formatDatasetValue(period.from)} → ${formatDatasetValue(period.to)}`;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return formatMetadataIdList(value);
  if (typeof value === 'object') return null;
  if (typeof value === 'number') {
    return formatDatasetValue(value, key);
  }
  if (typeof value === 'string' && (key.endsWith('_at') || key.includes('date'))) {
    const formatted = formatDatasetDateTime(value);
    return formatted !== '—' ? formatted : value;
  }
  if (typeof value === 'string' && isWeightFieldKey(key)) {
    return formatReactionWeightValue(value);
  }
  return String(value);
}

function buildDatasetMetadataItems(metadata: Record<string, unknown>): Array<{ label: string; value: React.ReactNode }> {
  const items: Array<{ label: string; value: React.ReactNode }> = [];
  const keys = Object.keys(metadata).filter((k) => !METADATA_PANEL_SKIP_KEYS.has(k));
  keys.sort((a, b) => {
    const ia = METADATA_KEY_ORDER.indexOf(a);
    const ib = METADATA_KEY_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });

  for (const key of keys) {
    const value = formatMetadataFieldValue(key, metadata[key]);
    if (value === null) continue;
    items.push({ label: formatDatasetFieldLabel(key), value });
  }

  if (metadata.training_period && typeof metadata.training_period === 'object') {
    items.push({
      label: formatDatasetFieldLabel('training_period'),
      value: formatMetadataFieldValue('training_period', metadata.training_period),
    });
  }
  if (metadata.filters && typeof metadata.filters === 'object') {
    items.push({
      label: formatDatasetFieldLabel('filters'),
      value: formatMetadataFilters(metadata.filters as Record<string, unknown>),
    });
  }
  const stats = metadata.stats;
  if (stats && typeof stats === 'object') {
    const s = stats as Record<string, unknown>;
    if (typeof s.dataset_tokens_original === 'number') {
      items.push({
        label: formatDatasetFieldLabel('dataset_tokens_original'),
        value: Number(s.dataset_tokens_original).toLocaleString(),
      });
    }
  }

  return items;
}

function buildIntegrationDatasetMetadataItems(metadata: Record<string, unknown>): Array<{ label: string; value: React.ReactNode }> {
  const items: Array<{ label: string; value: React.ReactNode }> = [];
  for (const key of INTEGRATION_METADATA_KEYS) {
    const value = formatMetadataFieldValue(key, metadata[key]);
    if (value === null || value === undefined || value === '') continue;
    items.push({ label: formatDatasetFieldLabel(key), value });
  }
  return items;
}

const DEFAULT_FIRST_MESSAGE = 'Здравствуйте. Пожалуйста представьтесь и расскажите о своей проблеме.';

function formatStartDate(dateString: string | null): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    // Format: 02 Feb 2026, 14:32
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  } catch {
    return '-';
  }
}

function formatDatasetValue(value: unknown, fieldKey?: string): string {
  return formatDatasetFieldValue(value, fieldKey);
}

function formatDatasetDateTime(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return '—';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleString();
}

function formatDatasetRatio(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const percent = value <= 1 ? value * 100 : value;
  return `${percent.toFixed(percent % 1 === 0 ? 0 : 2)}%`;
}

function formatMetadataIdList(v: unknown): string {
  return Array.isArray(v)
    ? (v as unknown[])
        .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
        .join(', ') || '—'
    : '—';
}

function renderExpandableDatasetText(
  value: React.ReactNode,
  opts?: { maxLength?: number; maxLines?: number }
): React.ReactNode {
  if (typeof value === 'string') {
    return (
      <TruncatedText
        text={value}
        maxLength={opts?.maxLength ?? 120}
        maxLines={opts?.maxLines ?? 3}
      />
    );
  }
  return value;
}

function formatMetadataFilters(filters: Record<string, unknown>): React.ReactNode {
  const entries = Object.entries(filters).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return '—';
  return (
    <div className="dataset-meta-nested">
      {entries.map(([k, v]) => (
        <div className="dataset-meta-nested__row" key={k}>
          <span className="dataset-meta-nested__key">{formatDatasetFieldLabel(k)}</span>
          <span className="dataset-meta-nested__value">
            {renderExpandableDatasetText(formatDatasetValue(v, k), { maxLength: 80, maxLines: 2 })}
          </span>
        </div>
      ))}
    </div>
  );
}

function DatasetMetaPanel({
  title,
  items,
}: {
  title?: string;
  items: Array<{ label: string; value: React.ReactNode }>;
}) {
  const visible = items.filter(
    (item) => item.value !== null && item.value !== undefined && item.value !== ''
  );
  if (visible.length === 0) return null;
  return (
    <div className="dataset-meta-panel">
      {title ? <h4 className="dataset-meta-panel__title">{title}</h4> : null}
      <div className="dataset-meta-grid">
        {visible.map((item) => (
          <div className="dataset-meta-grid__item" key={item.label}>
            <span className="dataset-meta-grid__label">{item.label}</span>
            <div className="dataset-meta-grid__value">
              {renderExpandableDatasetText(item.value, { maxLength: 120, maxLines: 3 })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function normalizeDatasetSplitRows(rows: unknown): unknown[] {
  if (Array.isArray(rows)) return rows;
  if (!rows || typeof rows !== 'object') return [];

  const entries = Object.entries(rows as Record<string, unknown>);
  if (entries.length === 0) return [];

  return entries.map(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const rowObject = value as Record<string, unknown>;
      return rowObject.id !== undefined ? rowObject : { id: key, ...rowObject };
    }
    return { id: key, turns: Array.isArray(value) ? value : [] };
  });
}

function renderDatasetSplit(
  title: 'train' | 'validation' | 'test',
  rows: unknown
): React.ReactNode {
  const list = normalizeDatasetSplitRows(rows);
  const splitClass = 'dataset-viewer-split';
  return (
    <div
      key={title}
      className={splitClass}
      style={{
        border: '1px solid #E3F0FB',
        borderRadius: 10,
        background: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <div
        className="dataset-viewer-split__title"
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #E3F0FB',
          background: '#FFFFFF',
          fontWeight: 700,
          fontSize: 13,
          color: '#0A2A47',
          textTransform: 'capitalize',
        }}
      >
        {title} ({list.length})
      </div>
      {list.length === 0 ? (
        <div style={{ padding: 12, fontSize: 12, color: '#5B7893' }}>Empty</div>
      ) : (
        <div className="dataset-viewer-split__scroll">
          {list.map((item, idx) => {
            const row = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
            const privacyRestricted = row.privacy_restricted === true;
            const turnsCountRaw = row.turns_count;
            const turnsCount =
              typeof turnsCountRaw === 'number' && Number.isFinite(turnsCountRaw)
                ? turnsCountRaw
                : typeof turnsCountRaw === 'string' && turnsCountRaw.trim() !== ''
                  ? Number.parseInt(turnsCountRaw, 10)
                  : null;
            const turns = privacyRestricted ? [] : Array.isArray(row.turns) ? row.turns : [];
            const rowId = formatDatasetValue(row.id ?? `${title}-${idx + 1}`);
            const rowExtraFields = Object.entries(row).filter(
              ([key]) =>
                key !== 'id' &&
                key !== 'turns' &&
                key !== 'privacy_restricted' &&
                key !== 'turns_count'
            );

            if (privacyRestricted) {
              return (
                <div
                  key={`${title}-${idx}`}
                  className="dataset-viewer-entry dataset-viewer-entry--privacy-restricted"
                  style={{
                    border: '1px solid #E3F0FB',
                    borderRadius: 8,
                    background: '#FFFFFF',
                  }}
                >
                  <div className="dataset-viewer-entry__summary dataset-viewer-entry__summary--static">
                    <TruncatedText text={rowId} maxLength={80} maxLines={1} showCopyButton={false} />
                    <span className="dataset-viewer-entry__meta">
                      • turns: {turnsCount !== null && !Number.isNaN(turnsCount) ? turnsCount : '—'}
                    </span>
                  </div>
                  <p className="dataset-viewer-entry__privacy-note">
                    Messages hidden by privacy settings.
                  </p>
                </div>
              );
            }

            return (
              <details
                key={`${title}-${idx}`}
                className="dataset-viewer-entry"
                style={{
                  border: '1px solid #E3F0FB',
                  borderRadius: 8,
                  background: '#FFFFFF',
                }}
              >
                <summary className="dataset-viewer-entry__summary">
                  <TruncatedText text={rowId} maxLength={80} maxLines={1} showCopyButton={false} />
                  <span className="dataset-viewer-entry__meta">• turns: {turns.length}</span>
                </summary>
                <div className="dataset-viewer-entry__body">
                  {rowExtraFields.length > 0 ? (
                    <div className="dataset-viewer-fields">
                      {rowExtraFields.map(([key, value]) => (
                        <div className="dataset-viewer-field" key={`${title}-${idx}-${key}`}>
                          <span className="dataset-viewer-field__label">{formatDatasetFieldLabel(key)}</span>
                          <div className="dataset-viewer-field__value">
                            {renderExpandableDatasetText(formatDatasetValue(value, key), {
                              maxLength: 160,
                              maxLines: 4,
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <DatasetViewerTurnList
                    turns={turns}
                    blockKeyPrefix={`${title}-${idx}`}
                    maxTurns={20}
                  />
                </div>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Portals embedded connector modals to body so they stay visible when the datasets tab is hidden. */
function renderEmbeddedOverlay(content: React.ReactNode, usePortal: boolean): React.ReactNode {
  if (!usePortal || typeof document === 'undefined') return content;
  return createPortal(content, document.body);
}

interface DatasetsCollapsibleSectionProps {
  id?: string;
  headId?: string;
  open: boolean;
  onToggle: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

function DatasetsCollapsibleSection({
  id,
  headId,
  open,
  onToggle,
  title,
  subtitle,
  actions,
  children,
}: DatasetsCollapsibleSectionProps) {
  return (
    <div
      id={id}
      className={`datasets-section-card${open ? '' : ' datasets-section-card--collapsed'}`}
    >
      <div className="datasets-section-head" id={headId}>
        <div className="datasets-section-head__lead">
          <button
            type="button"
            className="datasets-section-collapse-btn"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={open ? 'Collapse section' : 'Expand section'}
          >
            <i
              className={`fas fa-chevron-down datasets-section-collapse-icon${open ? '' : ' datasets-section-collapse-icon--closed'}`}
              aria-hidden
            />
          </button>
          <button type="button" className="datasets-section-head__title-btn" onClick={onToggle}>
            <h2>{title}</h2>
            {subtitle ? <p className="datasets-section-head__subtitle">{subtitle}</p> : null}
          </button>
        </div>
        {actions ? <div className="datasets-section-head__actions">{actions}</div> : null}
      </div>
      {open ? <div className="datasets-section-body">{children}</div> : null}
    </div>
  );
}

export interface DatasetsPageProps {
  integrationId?: string | null;
  onOpenOnboarding?: () => void;
  /** Render inside Connector tab — compact layout, no full-page chrome */
  embedded?: boolean;
  /** Open experiment details modal when set (e.g. from pipeline failure link). */
  openExperimentId?: string | null;
  onOpenExperimentHandled?: () => void;
  /** Notifies parent when a long-running modal is open (embedded connector tab keep-alive). */
  onBlockingModalOpenChange?: (open: boolean) => void;
}

const DatasetsPage: React.FC<DatasetsPageProps> = ({
  integrationId: propIntegrationId,
  onOpenOnboarding,
  embedded = false,
  openExperimentId,
  onOpenExperimentHandled,
  onBlockingModalOpenChange,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlIntegrationId = searchParams.get('integration_id');
  const integrationId = propIntegrationId ?? urlIntegrationId;

  const [loading, setLoading] = useState(false);
  const [retryingExperimentId, setRetryingExperimentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [virtualUsers, setVirtualUsers] = useState<VirtualUser[]>([]);

  const [connectorDatasets, setConnectorDatasets] = useState<ConnectorDataset[]>([]);
  const [connectorDatasetsLoading, setConnectorDatasetsLoading] = useState(false);
  const [datasetContentTableCache, setDatasetContentTableCache] = useState<Record<string, DatasetContentTableCacheEntry>>({});
  const datasetContentTableCacheRef = useRef<Record<string, DatasetContentTableCacheEntry>>({});
  const [importDatasetSectionOpen, setImportDatasetSectionOpen] = useState(true);
  const [vuserDatasetSectionOpen, setVuserDatasetSectionOpen] = useState(true);
  const [datasetViewerOpen, setDatasetViewerOpen] = useState(false);
  const [datasetViewerLoading, setDatasetViewerLoading] = useState(false);
  const [datasetViewerError, setDatasetViewerError] = useState<string | null>(null);
  const [datasetViewerData, setDatasetViewerData] = useState<DatasetContentResponse | null>(null);
  const [datasetViewerId, setDatasetViewerId] = useState<string | null>(null);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const exportModalBodyRef = useRef<HTMLDivElement | null>(null);
  const exportModalAutoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exportModalValidation = useModalFormValidation(exportModalBodyRef);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [exportForm, setExportForm] = useState<{
    dataset_name: string;
    from: string;
    to: string;
    train_ratio: number;
    validation_ratio: number;
    /** Дефолт 0 — деление остаётся двусторонним, пока пользователь не тронет тестовый слайдер. */
    test_ratio: number;
    limit_history: boolean;
    history_depth: number | string;
    reactions: ('like' | 'dislike')[];
    comment: boolean;
    groundtruth: boolean;
    reactionWeights: ReactionWeightsFormState;
  }>({
    dataset_name: '',
    from: '',
    to: '',
    train_ratio: 75,
    validation_ratio: 25,
    test_ratio: 0,
    limit_history: true,
    history_depth: DEFAULT_HISTORY_DEPTH,
    reactions: [...DEFAULT_EXPORT_SIGNAL_FILTERS.reactions],
    comment: DEFAULT_EXPORT_SIGNAL_FILTERS.comment,
    groundtruth: DEFAULT_EXPORT_SIGNAL_FILTERS.groundtruth,
    reactionWeights: { ...DEFAULT_REACTION_WEIGHTS_FORM },
  });

  const [activeImportJob, setActiveImportJob] = useState<DatasetImportJob | null>(null);
  const [importJobCancellingId, setImportJobCancellingId] = useState<string | null>(null);
  const exportImportJobIdRef = useRef<string | null>(null);
  const exportImportPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const exportImportCreateAbortRef = useRef<AbortController | null>(null);
  const exportModalOpenRef = useRef(false);
  const handleDatasetImportJobUpdateRef = useRef<(job: DatasetImportJob) => void>(() => undefined);
  const startDatasetImportJobPollingRef = useRef<(jobId: string) => void>(() => undefined);
  const exportModalValidationRef = useRef(exportModalValidation);
  const activeImportJobRef = useRef<DatasetImportJob | null>(null);

  const [exportPreviewData, setExportPreviewData] = useState<DatasetExportPreviewData | null>(null);
  const [exportPreviewLoading, setExportPreviewLoading] = useState(false);
  const exportPreviewAbortRef = useRef<AbortController | null>(null);
  const [exportSourceUsers, setExportSourceUsers] = useState<DatasetSourceUser[]>([]);
  const [exportSourceUsersLoading, setExportSourceUsersLoading] = useState(false);
  const [exportSourceDialogs, setExportSourceDialogs] = useState<DatasetSourceDialog[]>([]);
  const [exportSourceDialogsLoading, setExportSourceDialogsLoading] = useState(false);
  const [exportDialogsPage, setExportDialogsPage] = useState(1);
  const [exportDialogsMeta, setExportDialogsMeta] = useState({ total: 0, total_pages: 1, limit: 50 });
  const [exportSelectedUserIds, setExportSelectedUserIds] = useState<string[]>([]);
  const [exportSelectedDialogIds, setExportSelectedDialogIds] = useState<string[]>([]);
  const [exportSelectAllDialogsBusy, setExportSelectAllDialogsBusy] = useState(false);
  const exportAutoSelectUsersPendingRef = useRef(false);
  const exportAutoSelectDialogsPendingRef = useRef(false);
  const loadExportSourceDialogsSeqRef = useRef(0);
  const exportSelectAllDialogsSeqRef = useRef(0);
  const selectAllExportDialogsInScopeRef = useRef<(fallbackPageIds?: string[]) => Promise<void>>(
    async () => {}
  );
  const loadExportSourceDialogsRef = useRef<() => Promise<void>>(async () => {});

  const cancelExportPreviewRequest = useCallback(() => {
    exportPreviewAbortRef.current?.abort();
    exportPreviewAbortRef.current = null;
    setExportPreviewLoading(false);
  }, []);

  const cancelExportSelectAllDialogs = useCallback(() => {
    exportSelectAllDialogsSeqRef.current += 1;
    setExportSelectAllDialogsBusy(false);
  }, []);

  const exportHasAtLeastOneSignal = exportFormHasAtLeastOneSignal(exportForm);

  const activeImportJobRunning = isDatasetImportJobActive(activeImportJob?.status);

  const {
    displayed: importJobProgressDisplay,
    syncFromBackend: syncImportJobProgress,
    complete: completeImportJobProgress,
    reset: resetImportJobProgress,
  } = useJobsApiProgressDisplay({ expectedDurationSeconds: 90 });

  useEffect(() => {
    exportModalOpenRef.current = exportModalOpen;
  }, [exportModalOpen]);

  useEffect(() => {
    activeImportJobRef.current = activeImportJob;
  }, [activeImportJob]);

  useEffect(() => {
    datasetContentTableCacheRef.current = datasetContentTableCache;
  }, [datasetContentTableCache]);

  const exportSelectedDialogsOnPageCount = useMemo(() => {
    if (exportSelectedDialogIds.length === 0) return 0;
    const selected = new Set(exportSelectedDialogIds);
    return exportSourceDialogs.reduce((count, dialog) => {
      const id = (dialog.dialog_id || '').trim();
      return selected.has(id) ? count + 1 : count;
    }, 0);
  }, [exportSourceDialogs, exportSelectedDialogIds]);

  const isExportRequestCanceled = (e: unknown) =>
    (e as { code?: string; name?: string })?.code === 'ERR_CANCELED' ||
    (e as { name?: string })?.name === 'CanceledError';

  const stopExportImportPolling = useCallback(() => {
    if (exportImportPollRef.current) {
      clearInterval(exportImportPollRef.current);
      exportImportPollRef.current = null;
    }
    exportImportJobIdRef.current = null;
  }, []);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Experiment | null>(null);
  const [deleteModalError, setDeleteModalError] = useState<string | null>(null);

  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [scoreModalLoading, setScoreModalLoading] = useState(false);
  const [scoreModalError, setScoreModalError] = useState<string | null>(null);
  const [scoreModalExperiment, setScoreModalExperiment] = useState<Experiment | null>(null);
  const [scoreModalScore, setScoreModalScore] = useState<ExperimentScoreBreakdown | null>(null);
  const [scoreModalStats, setScoreModalStats] = useState<ExperimentTokenStatistics | null>(null);
  const [scoreModalDialogs, setScoreModalDialogs] = useState<Dialog[]>([]);

  const startExpModalBodyRef = useRef<HTMLDivElement | null>(null);
  const startExpModalValidation = useModalFormValidation(startExpModalBodyRef);
  const [startExpOpen, setStartExpOpen] = useState(false);

  useEffect(() => {
    if (!embedded) return;
    onBlockingModalOpenChange?.(exportModalOpen || startExpOpen);
  }, [embedded, exportModalOpen, startExpOpen, onBlockingModalOpenChange]);
  const [startExp, setStartExp] = useState<{
    experiment_name: string;
    useCustomPersonaPairs: boolean;
    virtual_user_ids: string[];
    virtual_admin_ids: string[];
    userPersonaPairs: ExperimentPersonaPairRow[];
    adminPersonaPairs: ExperimentPersonaPairRow[];
    first_message: string;
    dialog_count: string;
    messages_per_dialog: string;
    /** Пусто — начать с входного стейта графа, как было до появления поля. */
    entry_state_id: string;
    limit_history: boolean;
    history_depth: string;
    train_ratio: number;
    validation_ratio: number;
    reactionWeights: ReactionWeightsFormState;
  }>({
    experiment_name: '',
    useCustomPersonaPairs: false,
    virtual_user_ids: [],
    virtual_admin_ids: [],
    userPersonaPairs: [],
    adminPersonaPairs: [],
    first_message: DEFAULT_FIRST_MESSAGE,
    dialog_count: '10',
    messages_per_dialog: '5',
    entry_state_id: '',
        limit_history: true,
    history_depth: String(DEFAULT_HISTORY_DEPTH),
    train_ratio: 75,
    validation_ratio: 25,
    reactionWeights: { ...EMPTY_REACTION_WEIGHTS_FORM },
  });

  const [workflowStates, setWorkflowStates] = useState<WorkflowStatesSnapshot | null>(null);
  const [workflowStatesStatus, setWorkflowStatesStatus] =
    useState<'idle' | 'loading' | 'ready' | 'absent' | 'error'>('idle');

  const [assistantConfigEnvelope, setAssistantConfigEnvelope] = useState<Record<string, unknown> | null>(
    null
  );
  const [tokenPreviewLoading, setTokenPreviewLoading] = useState(false);
  const [tokenPreviewError, setTokenPreviewError] = useState<string | null>(null);
  const [tokenPreviewResult, setTokenPreviewResult] =
    useState<DatasetGenerationPredictTokenUsageResponse | null>(null);

  const exportPeriodParams = useMemo(() => {
    const { from, to } = exportForm;
    if (!from || !to) return null;
    const fromDate = new Date(from.length <= 10 ? `${from}T00:00:00` : from);
    const toDate = new Date(to.length <= 10 ? `${to}T23:59:59` : to);
    return { from: fromDate.toISOString(), to: toDate.toISOString() };
  }, [exportForm.from, exportForm.to]);

  const buildExportValidation = useCallback(
    (requireDatasetName: boolean) =>
      validateExportForm({
        dataset_name: exportForm.dataset_name,
        requireDatasetName,
        hasPeriod: Boolean(exportPeriodParams),
        hasAtLeastOneSignal: exportHasAtLeastOneSignal,
        selectedUserCount: exportSelectedUserIds.length,
        selectedDialogCount: exportSelectedDialogIds.length,
        reactionWeights: exportForm.reactionWeights,
      }),
    [
      exportForm.dataset_name,
      exportForm.reactionWeights,
      exportPeriodParams,
      exportHasAtLeastOneSignal,
      exportSelectedUserIds.length,
      exportSelectedDialogIds.length,
    ]
  );

  const loadExportSourceUsers = useCallback(async () => {
    if (!integrationId || !exportModalOpen || !exportPeriodParams) {
      if (!exportModalOpen) setExportSourceUsers([]);
      return;
    }
    setExportSourceUsersLoading(true);
    try {
      const res = await connector.listDatasetSourceUsers(integrationId, {
        from: exportPeriodParams.from,
        to: exportPeriodParams.to,
      });
      const raw = res?.data as { users?: DatasetSourceUser[] } | undefined;
      const users = raw?.users ?? (res as { users?: DatasetSourceUser[] }).users ?? [];
      setExportSourceUsers(Array.isArray(users) ? users : []);
    } catch {
      setExportSourceUsers([]);
    } finally {
      setExportSourceUsersLoading(false);
    }
  }, [integrationId, exportModalOpen, exportPeriodParams]);

  const selectAllExportDialogsInScope = useCallback(async (fallbackPageIds: string[] = []) => {
    if (!integrationId || !exportPeriodParams || exportSelectedUserIds.length === 0) return;
    const seq = ++exportSelectAllDialogsSeqRef.current;
    setExportSelectAllDialogsBusy(true);
    const collected: string[] = [];
    try {
      let page = 1;
      let totalPages = 1;
      const limit = exportDialogsMeta.limit || 50;
      while (page <= totalPages) {
        if (seq !== exportSelectAllDialogsSeqRef.current) return;
        const res = await connector.listDatasetSourceDialogs(integrationId, {
          page,
          limit,
          from: exportPeriodParams.from,
          to: exportPeriodParams.to,
          user_ids: exportSelectedUserIds,
        });
        if (seq !== exportSelectAllDialogsSeqRef.current) return;
        const envelope = (res?.data ?? res) as {
          dialogs?: DatasetSourceDialog[];
          total_pages?: number;
        };
        const dialogs = Array.isArray(envelope.dialogs) ? envelope.dialogs : [];
        for (const dialog of dialogs) {
          const id = (dialog.dialog_id || '').trim();
          if (id) collected.push(id);
        }
        totalPages = Math.max(1, typeof envelope.total_pages === 'number' ? envelope.total_pages : 1);
        page += 1;
      }
      if (seq !== exportSelectAllDialogsSeqRef.current) return;
      setExportSelectedDialogIds(Array.from(new Set(collected)));
    } catch {
      if (seq !== exportSelectAllDialogsSeqRef.current) return;
      if (collected.length > 0) {
        setExportSelectedDialogIds(Array.from(new Set(collected)));
        return;
      }
      if (fallbackPageIds.length > 0) {
        setExportSelectedDialogIds(Array.from(new Set(fallbackPageIds)));
      }
    } finally {
      if (seq === exportSelectAllDialogsSeqRef.current) {
        setExportSelectAllDialogsBusy(false);
      }
    }
  }, [integrationId, exportPeriodParams, exportSelectedUserIds, exportDialogsMeta.limit]);

  useEffect(() => {
    selectAllExportDialogsInScopeRef.current = selectAllExportDialogsInScope;
  }, [selectAllExportDialogsInScope]);

  const loadExportSourceDialogs = useCallback(async () => {
    if (!integrationId || !exportModalOpen || !exportPeriodParams) {
      if (!exportModalOpen) setExportSourceDialogs([]);
      return;
    }
    if (exportSelectedUserIds.length === 0) {
      setExportSourceDialogs([]);
      setExportDialogsMeta({ total: 0, total_pages: 1, limit: 50 });
      setExportSourceDialogsLoading(false);
      return;
    }
    const seq = ++loadExportSourceDialogsSeqRef.current;
    setExportSourceDialogsLoading(true);
    let fetchedTotal = 0;
    let fetchedDialogs: DatasetSourceDialog[] = [];
    try {
      const res = await connector.listDatasetSourceDialogs(integrationId, {
        page: exportDialogsPage,
        limit: 50,
        from: exportPeriodParams.from,
        to: exportPeriodParams.to,
        user_ids: exportSelectedUserIds,
      });
      if (seq !== loadExportSourceDialogsSeqRef.current) return;
      const envelope = (res?.data ?? res) as {
        dialogs?: DatasetSourceDialog[];
        total?: number;
        total_pages?: number;
        limit?: number;
      };
      fetchedTotal = typeof envelope.total === 'number' ? envelope.total : 0;
      fetchedDialogs = Array.isArray(envelope.dialogs) ? envelope.dialogs : [];
      setExportSourceDialogs(fetchedDialogs);
      setExportDialogsMeta({
        total: fetchedTotal,
        total_pages: Math.max(1, typeof envelope.total_pages === 'number' ? envelope.total_pages : 1),
        limit: typeof envelope.limit === 'number' ? envelope.limit : 50,
      });
    } catch {
      if (seq !== loadExportSourceDialogsSeqRef.current) return;
      setExportSourceDialogs([]);
      setExportDialogsMeta({ total: 0, total_pages: 1, limit: 50 });
    } finally {
      if (seq === loadExportSourceDialogsSeqRef.current) {
        setExportSourceDialogsLoading(false);
      }
    }

    if (seq !== loadExportSourceDialogsSeqRef.current) return;
    if (!exportAutoSelectDialogsPendingRef.current || exportSelectedUserIds.length === 0) return;

    exportAutoSelectDialogsPendingRef.current = false;
    if (fetchedTotal > 0) {
      const pageIds = fetchedDialogs
        .map((d) => (d.dialog_id || '').trim())
        .filter(Boolean);
      if (pageIds.length > 0) {
        setExportSelectedDialogIds(Array.from(new Set(pageIds)));
      }
      void selectAllExportDialogsInScopeRef.current(pageIds);
    }
  }, [
    integrationId,
    exportModalOpen,
    exportPeriodParams,
    exportDialogsPage,
    exportSelectedUserIds,
  ]);

  useEffect(() => {
    loadExportSourceDialogsRef.current = loadExportSourceDialogs;
  }, [loadExportSourceDialogs]);

  const deselectAllExportDialogs = useCallback(() => {
    setExportSelectedDialogIds([]);
  }, []);

  const selectAllExportUsers = useCallback(() => {
    const ids = exportSourceUsers
      .map((u) => (u.user_id || '').trim())
      .filter(Boolean);
    setExportSelectedUserIds(Array.from(new Set(ids)));
  }, [exportSourceUsers]);

  const deselectAllExportUsers = useCallback(() => {
    setExportSelectedUserIds([]);
  }, []);

  const selectExportDialogsOnPage = useCallback(() => {
    const pageIds = exportSourceDialogs
      .map((d) => (d.dialog_id || '').trim())
      .filter(Boolean);
    if (pageIds.length === 0) return;
    setExportSelectedDialogIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  }, [exportSourceDialogs]);

  useEffect(() => {
    if (!exportModalOpen || !integrationId || !exportPeriodParams) return;
    void loadExportSourceUsers();
  }, [exportModalOpen, integrationId, exportPeriodParams, loadExportSourceUsers]);

  useEffect(() => {
    if (!exportModalOpen || !integrationId || !exportPeriodParams) return;
    void loadExportSourceDialogsRef.current();
  }, [
    exportModalOpen,
    integrationId,
    exportPeriodParams,
    exportDialogsPage,
    exportSelectedUserIds.join('\u001f'),
  ]);

  useEffect(() => {
    if (!exportModalOpen) return;
    cancelExportPreviewRequest();
    cancelExportSelectAllDialogs();
    setExportDialogsPage(1);
    setExportSelectedUserIds([]);
    setExportSelectedDialogIds([]);
    setExportPreviewData(null);
    setExportDialogsMeta({ total: 0, total_pages: 1, limit: 50 });
    exportAutoSelectUsersPendingRef.current = true;
    exportAutoSelectDialogsPendingRef.current = true;
  }, [
    exportModalOpen,
    exportForm.from,
    exportForm.to,
    cancelExportPreviewRequest,
    cancelExportSelectAllDialogs,
  ]);

  useEffect(() => {
    if (!exportModalOpen) return;
    cancelExportSelectAllDialogs();
    setExportDialogsPage(1);
    setExportSelectedDialogIds([]);
    setExportPreviewData(null);
    setExportDialogsMeta({ total: 0, total_pages: 1, limit: 50 });
    exportAutoSelectDialogsPendingRef.current = true;
  }, [exportModalOpen, exportSelectedUserIds.join('\u001f'), cancelExportSelectAllDialogs]);

  useEffect(() => {
    if (!exportModalOpen || exportSourceUsersLoading) return;
    if (!exportAutoSelectUsersPendingRef.current) return;
    exportAutoSelectUsersPendingRef.current = false;
    if (exportSourceUsers.length === 0) return;
    selectAllExportUsers();
  }, [exportModalOpen, exportSourceUsersLoading, exportSourceUsers, selectAllExportUsers]);

  useEffect(() => {
    if (!exportModalOpen) return;
    setExportPreviewData(null);
  }, [exportModalOpen, exportForm.reactions, exportForm.comment, exportForm.groundtruth, exportForm.limit_history, exportForm.history_depth]);

  useEffect(() => {
    if (!exportModalOpen) return;
    setExportPreviewData(null);
  }, [exportModalOpen, exportSelectedDialogIds]);

  // Keep errors visible until the next action; success auto-hides.
  const clearSuccessSoon = () => setTimeout(() => setSuccess(null), NOTIFICATION_AUTO_HIDE_MS);

  useEffect(() => {
    if (!exportModalOpen) return;
    exportModalValidation.revalidateIfNeeded(buildExportValidation(true));
  }, [
    exportForm,
    exportPeriodParams,
    exportHasAtLeastOneSignal,
    exportSelectedUserIds.length,
    exportSelectedDialogIds.length,
    exportModalOpen,
    buildExportValidation,
    exportModalValidation.revalidateIfNeeded,
  ]);

  const loadExperiments = async (options?: { silent?: boolean }) => {
    if (!integrationId) return;
    if (!options?.silent) {
    setLoading(true);
    }
    setError(null);
    try {
      const resp = await vuser.listExperiments(integrationId);
      const experiments = resp.data || [];
      // Sort by started_at (most recent first), then by created_at if started_at is null
      const sortedExperiments = [...experiments].sort((a, b) => {
        const aDate = a.started_at || a.created_at;
        const bDate = b.started_at || b.created_at;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
      setExperiments(sortedExperiments);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status !== 401) {
        setExperiments([]);
      }
      if (!options?.silent) {
      setError(getErrorMessage(e, 'Failed to load datasets'));
      }
    } finally {
      if (!options?.silent) {
      setLoading(false);
      }
    }
  };

  const loadVirtualUsers = async () => {
    if (!integrationId) return;
    try {
      const resp = await vuser.listVirtualUsers(integrationId);
      const users = resp.data || [];
      setVirtualUsers(users);
    } catch (e: any) {
      setVirtualUsers([]);
      console.error('Failed to load virtual users:', e);
    }
  };

  const loadConnectorDatasets = useCallback(async (opts?: { silent?: boolean }) => {
    if (!integrationId) return;
    if (!opts?.silent) setConnectorDatasetsLoading(true);
    try {
      const resp = await connector.getDatasets(integrationId);
      const datasets = resp?.data ?? [];
      setConnectorDatasets(Array.isArray(datasets) ? datasets : []);
    } catch (e) {
      console.error('Failed to load connector datasets:', e);
      setConnectorDatasets([]);
    } finally {
      if (!opts?.silent) setConnectorDatasetsLoading(false);
    }
  }, [integrationId]);

  const resolveDatasetId = useCallback((row: ConnectorDataset): string | null => {
    const candidate = (row.id ?? (row as Record<string, unknown>).dataset_id ?? (row as Record<string, unknown>).datasetId) as
      | string
      | undefined;
    const text = typeof candidate === 'string' ? candidate.trim() : '';
    return text || null;
  }, []);

  useEffect(() => {
    datasetContentTableCacheRef.current = {};
    setDatasetContentTableCache({});
  }, [integrationId]);

  useEffect(() => {
    if (!integrationId || connectorDatasets.length === 0) return;
    const ids = Array.from(
      new Set(connectorDatasets.map((row) => resolveDatasetId(row)).filter((id): id is string => Boolean(id)))
    );
    const missing = ids.filter((id) => !datasetContentTableCacheRef.current[id]);
    if (missing.length === 0) return;
    const placeholders = { ...datasetContentTableCacheRef.current };
    missing.forEach((id) => {
      placeholders[id] = { loaded: false, stats: null, warning: null };
    });
    datasetContentTableCacheRef.current = placeholders;
    setDatasetContentTableCache(placeholders);

    let cancelled = false;
    void (async () => {
      const results = await Promise.all(
        missing.map(async (id) => {
          try {
            const resp = await connector.getDatasetContent(id, integrationId);
            const data = resp?.data;
            const metadata = parseDatasetMetadata(data?.meta?.metadata);
            const metadataWarning = metadata?.warning;
            const warning =
              metadataWarning && typeof metadataWarning === 'object' && !Array.isArray(metadataWarning)
                ? (metadataWarning as DatasetDataWarning)
                : null;
            return {
              id,
              entry: {
                loaded: true,
                stats: data?.stats ? (data.stats as Record<string, unknown>) : null,
                warning,
              },
            };
          } catch {
            return {
              id,
              entry: { loaded: true, stats: null, warning: null },
            };
          }
        })
      );
      if (cancelled) return;
      setDatasetContentTableCache((prev) => {
        const next = { ...prev };
        results.forEach(({ id, entry }) => {
          next[id] = entry;
        });
        datasetContentTableCacheRef.current = next;
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [connectorDatasets, integrationId, resolveDatasetId]);

  const closeDatasetViewer = useCallback(() => {
    setDatasetViewerOpen(false);
    setDatasetViewerLoading(false);
    setDatasetViewerError(null);
    setDatasetViewerData(null);
    setDatasetViewerId(null);
  }, []);

  const openDatasetViewer = useCallback(
    async (row: ConnectorDataset) => {
      if (!integrationId) return;
      const datasetId = resolveDatasetId(row);
      if (!datasetId) {
        setDatasetViewerError('Dataset id is missing for this row.');
        setDatasetViewerData(null);
        setDatasetViewerId(null);
        setDatasetViewerOpen(true);
        return;
      }
      setDatasetViewerOpen(true);
      setDatasetViewerLoading(true);
      setDatasetViewerError(null);
      setDatasetViewerData(null);
      setDatasetViewerId(datasetId);
      try {
        const resp = await connector.getDatasetContent(datasetId, integrationId);
        if (resp?.success === false) {
          setDatasetViewerError(resp.error?.message || 'Failed to load dataset content');
          return;
        }
        const data = resp?.data ?? null;
        setDatasetViewerData(data);
        if (data) {
          const metadata = parseDatasetMetadata(data.meta?.metadata);
          const metadataWarning = metadata?.warning;
          const warning =
            metadataWarning && typeof metadataWarning === 'object' && !Array.isArray(metadataWarning)
              ? (metadataWarning as DatasetDataWarning)
              : null;
          setDatasetContentTableCache((prev) => {
            const next = {
              ...prev,
              [datasetId]: {
                loaded: true,
                stats: data.stats ? (data.stats as Record<string, unknown>) : null,
                warning,
              },
            };
            datasetContentTableCacheRef.current = next;
            return next;
          });
        }
      } catch (e) {
        setDatasetViewerError(getErrorMessage(e, 'Failed to load dataset content'));
      } finally {
        setDatasetViewerLoading(false);
      }
    },
    [integrationId, resolveDatasetId]
  );

  const openExportModal = () => {
    const { from, to } = getDefaultTrainingDateRange();
    setExportForm({
      dataset_name: '',
      from,
      to,
      train_ratio: 75,
      validation_ratio: 25,
      test_ratio: 0,
      limit_history: true,
      history_depth: DEFAULT_HISTORY_DEPTH,
      reactions: [...DEFAULT_EXPORT_SIGNAL_FILTERS.reactions],
      comment: DEFAULT_EXPORT_SIGNAL_FILTERS.comment,
      groundtruth: DEFAULT_EXPORT_SIGNAL_FILTERS.groundtruth,
      reactionWeights: { ...DEFAULT_REACTION_WEIGHTS_FORM },
    });
    exportModalValidation.clearErrors();
    setExportSuccess(null);
    setError(null);
    setExportSelectedUserIds([]);
    setExportSelectedDialogIds([]);
    setExportDialogsPage(1);
    setExportPreviewData(null);
    setExportDialogsMeta({ total: 0, total_pages: 1, limit: 50 });
    setExportSourceUsers([]);
    setExportSourceDialogs([]);
    exportAutoSelectUsersPendingRef.current = true;
    exportAutoSelectDialogsPendingRef.current = true;
    setExportModalOpen(true);
  };

  const dismissActiveImportJob = useCallback(() => {
    if (activeImportJob && isDatasetImportJobActive(activeImportJob.status)) return;
    activeImportJobRef.current = null;
    setActiveImportJob(null);
  }, [activeImportJob]);

  const closeExportModal = useCallback(() => {
    if (exportModalAutoCloseRef.current) {
      clearTimeout(exportModalAutoCloseRef.current);
      exportModalAutoCloseRef.current = null;
    }
    cancelExportPreviewRequest();
    exportImportCreateAbortRef.current?.abort();
    exportImportCreateAbortRef.current = null;
    cancelExportSelectAllDialogs();
    if (!exportImportJobIdRef.current) {
      setExportLoading(false);
    }
    setExportModalOpen(false);
    exportModalValidation.clearErrors();
    setExportSuccess(null);
    setExportPreviewData(null);
  }, [exportModalValidation, cancelExportPreviewRequest, cancelExportSelectAllDialogs]);

  const reportDatasetImportError = useCallback((error: unknown, fallback = 'Failed to import dataset') => {
    const friendly =
      typeof error === 'string'
        ? formatDatasetImportErrorMessage(error)
        : resolveDatasetImportApiError(error, fallback);
    setExportSuccess(null);
    exportModalValidationRef.current.reportApiError(friendly, EXPORT_ERROR_BANNER_ID);
  }, []);

  const handleDatasetImportJobUpdate = useCallback(
    (job: DatasetImportJob) => {
      const prev = activeImportJobRef.current;
      if (!shouldUpdateDatasetImportJob(prev, job)) return;

      activeImportJobRef.current = job;
      setActiveImportJob(job);

      if (job.status === 'completed') {
        completeImportJobProgress();
        stopExportImportPolling();
        if (integrationId) clearPersistedDatasetImportJobId(integrationId);
        setExportLoading(false);
        const msg = buildDatasetImportSuccessMessage(job);
        setSuccess(msg);
        clearSuccessSoon();
        setError(null);
        setImportJobCancellingId((current) => (current === job.id ? null : current));
        if (exportModalOpenRef.current) {
          setExportSuccess(msg);
          if (exportModalAutoCloseRef.current) {
            clearTimeout(exportModalAutoCloseRef.current);
          }
          exportModalAutoCloseRef.current = setTimeout(() => {
            exportModalAutoCloseRef.current = null;
            closeExportModal();
          }, DATASET_IMPORT_MODAL_SUCCESS_AUTO_CLOSE_MS);
        }
        void loadConnectorDatasets({ silent: true });
        return;
      }

      if (job.status === 'failed') {
        resetImportJobProgress();
        stopExportImportPolling();
        if (integrationId) clearPersistedDatasetImportJobId(integrationId);
        setExportLoading(false);
        setExportSuccess(null);
        setError(null);
        exportModalValidationRef.current.clearErrors();
        setImportJobCancellingId((current) => (current === job.id ? null : current));
        return;
      }

      if (job.status === 'cancelled') {
        resetImportJobProgress();
        stopExportImportPolling();
        if (integrationId) clearPersistedDatasetImportJobId(integrationId);
        setExportLoading(false);
        setExportSuccess(null);
        setError(null);
        exportModalValidationRef.current.clearErrors();
        setImportJobCancellingId((current) => (current === job.id ? null : current));
        return;
      }

      if (!isDatasetImportJobActive(job.status)) {
        setImportJobCancellingId((current) => (current === job.id ? null : current));
      }

      if (isDatasetImportJobActive(job.status)) {
        syncImportJobProgress(resolveDatasetImportJobProgressPercent(job));
      }
    },
    [
      clearSuccessSoon,
      closeExportModal,
      completeImportJobProgress,
      integrationId,
      loadConnectorDatasets,
      resetImportJobProgress,
      stopExportImportPolling,
      syncImportJobProgress,
    ]
  );

  const handleCancelDatasetImportJob = useCallback(async () => {
    const job = activeImportJobRef.current;
    if (!job?.id || !isDatasetImportJobActive(job.status) || importJobCancellingId) return;

    setImportJobCancellingId(job.id);
    setError(null);
    try {
      const res = await connector.cancelDatasetImportJob(job.id);
      if (res && res.success === false) {
        const raw =
          extractHttpErrorBodyMessage(res) ||
          extractHttpErrorBodyMessage(res.error as unknown) ||
          'Failed to cancel import';
        reportDatasetImportError(raw, 'Failed to cancel import');
        setImportJobCancellingId((current) => (current === job.id ? null : current));
        return;
      }
      const updated = res?.data;
      if (updated) {
        handleDatasetImportJobUpdateRef.current(updated);
        return;
      }
      const pollRes = await connector.getDatasetImportJob(job.id);
      if (pollRes?.data) {
        handleDatasetImportJobUpdateRef.current(pollRes.data);
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number } };
      if (err.response?.status === 409) {
        try {
          const pollRes = await connector.getDatasetImportJob(job.id);
          if (pollRes?.data) {
            handleDatasetImportJobUpdateRef.current(pollRes.data);
            return;
          }
        } catch {
          // fall through to generic error
        }
      }
      reportDatasetImportError(e, 'Failed to cancel import');
      setImportJobCancellingId((current) => (current === job.id ? null : current));
    }
  }, [importJobCancellingId, reportDatasetImportError]);

  const startDatasetImportJobPolling = useCallback(
    (jobId: string) => {
      if (exportImportJobIdRef.current === jobId && exportImportPollRef.current) return;

      stopExportImportPolling();
      exportImportJobIdRef.current = jobId;
      if (integrationId) persistDatasetImportJobId(integrationId, jobId);

      const pollJob = async () => {
        if (exportImportJobIdRef.current !== jobId) return;
        try {
          const res = await connector.getDatasetImportJob(jobId);
          if (exportImportJobIdRef.current !== jobId) return;
          if (res && res.success === false) {
            stopExportImportPolling();
            if (integrationId) clearPersistedDatasetImportJobId(integrationId);
            setExportLoading(false);
            reportDatasetImportError(res, 'Failed to check import status');
            return;
          }
          const job = res?.data;
          if (!job) return;
          handleDatasetImportJobUpdateRef.current(job);
          if (isDatasetImportJobTerminal(job.status)) {
            stopExportImportPolling();
          }
        } catch (e: unknown) {
          if (exportImportJobIdRef.current !== jobId) return;
          stopExportImportPolling();
          if (integrationId) clearPersistedDatasetImportJobId(integrationId);
          setExportLoading(false);
          reportDatasetImportError(e, 'Failed to check import status');
        }
      };

      void pollJob();
      exportImportPollRef.current = setInterval(() => {
        void pollJob();
      }, DATASET_IMPORT_POLL_INTERVAL_MS);
    },
    [integrationId, reportDatasetImportError, stopExportImportPolling]
  );

  useEffect(() => {
    handleDatasetImportJobUpdateRef.current = handleDatasetImportJobUpdate;
  }, [handleDatasetImportJobUpdate]);

  useEffect(() => {
    startDatasetImportJobPollingRef.current = startDatasetImportJobPolling;
  }, [startDatasetImportJobPolling]);

  useEffect(() => {
    exportModalValidationRef.current = exportModalValidation;
  }, [exportModalValidation]);

  useEffect(() => {
    if (!integrationId) {
      stopExportImportPolling();
      activeImportJobRef.current = null;
      setActiveImportJob(null);
      return;
    }
    const persistedJobId = readPersistedDatasetImportJobId(integrationId);
    if (persistedJobId && exportImportJobIdRef.current === persistedJobId) return;

    stopExportImportPolling();
    activeImportJobRef.current = null;
    setActiveImportJob(null);
    if (persistedJobId) {
      startDatasetImportJobPollingRef.current(persistedJobId);
    }
  }, [integrationId, stopExportImportPolling]);

  useEffect(() => {
    return () => {
      stopExportImportPolling();
    };
  }, [stopExportImportPolling]);

  useEffect(() => {
    if (!exportSuccess) return;
    const scrollToTop = () => {
      exportModalBodyRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    };
    scrollToTop();
    const raf = requestAnimationFrame(scrollToTop);
    return () => cancelAnimationFrame(raf);
  }, [exportSuccess]);

  const buildExportRequestBody = (includeDatasetName: boolean, includeTrainSplit: boolean): DatasetExportRequest | null => {
    if (!integrationId || !exportPeriodParams) return null;
    const body: DatasetExportRequest = {
      integration_id: integrationId,
      training_period: { from: exportPeriodParams.from, to: exportPeriodParams.to },
      limit_history: exportForm.limit_history,
      history_depth: exportForm.limit_history ? clampHistoryDepth(exportForm.history_depth, DEFAULT_HISTORY_DEPTH) : null,
      filters: {
        reactions: [...exportForm.reactions],
        comment: exportForm.comment,
        groundtruth: exportForm.groundtruth,
      },
    };
    if (includeTrainSplit) {
      body.train_ratio = exportForm.train_ratio / 100;
      body.validation_ratio = exportForm.validation_ratio / 100;
      // Ноль не отправляем: старые вызовы без теста должны остаться двусторонними на проводе.
      if (exportForm.test_ratio > 0) {
        body.test_ratio = exportForm.test_ratio / 100;
      }
    }
    if (includeDatasetName && exportForm.dataset_name.trim()) {
      body.dataset_name = exportForm.dataset_name.trim();
    }
    body.user_ids = [...exportSelectedUserIds];
    body.dialog_ids = [...exportSelectedDialogIds];
    const reaction_weights = buildReactionWeightsFromForm(exportForm.reactionWeights);
    if (reaction_weights) {
      body.reaction_weights = reaction_weights;
    }
    return body;
  };

  const handleExportPreview = async () => {
    if (!integrationId) return;
    if (exportModalValidation.reportValidation(buildExportValidation(false))) return;
    const body = buildExportRequestBody(false, false);
    if (!body) return;
    exportPreviewAbortRef.current?.abort();
    const ac = new AbortController();
    exportPreviewAbortRef.current = ac;
    setExportPreviewLoading(true);
    exportModalValidation.clearErrors();
    try {
      const res = await connector.exportDatasetPreview(body, { signal: ac.signal });
      if (res && res.success === false) {
        reportDatasetImportError(res, 'Preview failed');
        setExportPreviewData(null);
        return;
      }
      setExportPreviewData(res?.data ?? null);
    } catch (e: unknown) {
      if (isExportRequestCanceled(e)) return;
      reportDatasetImportError(e, 'Preview failed');
      setExportPreviewData(null);
    } finally {
      if (exportPreviewAbortRef.current === ac) {
        exportPreviewAbortRef.current = null;
        setExportPreviewLoading(false);
      }
    }
  };

  const handleExportSubmit = async () => {
    if (!integrationId) return;
    if (activeImportJobRunning) return;
    if (exportModalValidation.reportValidation(buildExportValidation(true))) return;
    const exportBody = buildExportRequestBody(true, true);
    if (!exportBody) {
      exportModalValidation.reportValidation(
        validateExportForm({
          dataset_name: exportForm.dataset_name,
          requireDatasetName: true,
          hasPeriod: false,
          hasAtLeastOneSignal: exportHasAtLeastOneSignal,
          selectedUserCount: exportSelectedUserIds.length,
          selectedDialogCount: exportSelectedDialogIds.length,
          reactionWeights: exportForm.reactionWeights,
        })
      );
      return;
    }
    setExportLoading(true);
    exportModalValidation.clearErrors();
    setExportSuccess(null);
    stopExportImportPolling();
    exportImportCreateAbortRef.current?.abort();
    const ac = new AbortController();
    exportImportCreateAbortRef.current = ac;
    try {
      const res = await connector.createDatasetImportJob(exportBody, { signal: ac.signal });

      if (res && res.success === false) {
        reportDatasetImportError(res);
        setExportLoading(false);
        return;
      }

      const job = res?.data;
      if (!job?.id) {
        reportDatasetImportError(res ?? 'Import did not return a job id. Check filters, period, and dataset name.');
        setExportLoading(false);
        return;
      }

      setActiveImportJob(job);
      activeImportJobRef.current = job;
      resetImportJobProgress();
      syncImportJobProgress(resolveDatasetImportJobProgressPercent(job));
      startDatasetImportJobPolling(job.id);
    } catch (e: unknown) {
      if (isExportRequestCanceled(e)) {
        setExportLoading(false);
        return;
      }
      reportDatasetImportError(e);
      setExportLoading(false);
    } finally {
      if (exportImportCreateAbortRef.current === ac) {
        exportImportCreateAbortRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (integrationId) {
      void loadExperiments();
      void loadVirtualUsers();
      void loadConnectorDatasets();
    } else {
      setExperiments([]);
      setVirtualUsers([]);
      setConnectorDatasets([]);
      setError(null);
    }
  }, [integrationId, loadConnectorDatasets]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && integrationId) {
        void loadConnectorDatasets();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [integrationId, loadConnectorDatasets]);

  useEffect(() => {
    const hasActiveExperiment = experiments.some(
      (ex) => ex.status === 'running' || ex.status === 'queued'
    );
    if (!integrationId || !hasActiveExperiment) return;
    const timer = window.setInterval(() => {
      void loadExperiments({ silent: true });
    }, 2000);
    return () => window.clearInterval(timer);
  }, [integrationId, experiments]);

  /** Selected virtual users (type user) that have no persona assigned — warn in default selection mode. */
  const virtualUsersMissingPersona = useMemo(() => {
    if (startExp.useCustomPersonaPairs) return [];
    return startExp.virtual_user_ids
      .map((id) => virtualUsers.find((u) => u.id === id))
      .filter((u): u is VirtualUser => Boolean(u && u.type === 'user' && !u.persona_id));
  }, [startExp.useCustomPersonaPairs, startExp.virtual_user_ids, virtualUsers]);

  const buildStartExpValidation = useCallback(() => {
    const userPersonaPairs = rowsToBindings(startExp.userPersonaPairs).filter(
      (p) => p.virtual_user_id && p.persona_id
    );
    const adminPersonaPairs = rowsToBindings(startExp.adminPersonaPairs).filter(
      (p) => p.virtual_user_id && p.persona_id
    );
    const vu = startExp.virtual_user_ids.filter(Boolean);
    return validateStartExperimentForm({
      experiment_name: startExp.experiment_name,
      useCustomPersonaPairs: startExp.useCustomPersonaPairs,
      userPersonaPairs,
      adminPersonaPairs,
      virtualUserIds: vu,
      virtualUsersMissingPersonaNames: virtualUsersMissingPersona.map((u) => u.name),
      first_message: startExp.first_message.trim(),
      dialog_count: Number(startExp.dialog_count),
      messages_per_dialog: Number(startExp.messages_per_dialog),
      maxDialogs: MAX_EXPERIMENT_DIALOGS,
      maxMessagesPerDialog: MAX_MESSAGES_PER_DIALOG,
      reactionWeights: startExp.reactionWeights,
    });
  }, [startExp, virtualUsersMissingPersona]);

  useEffect(() => {
    if (!startExpOpen) return;
    startExpModalValidation.revalidateIfNeeded(buildStartExpValidation());
  }, [startExp, startExpOpen, virtualUsers, buildStartExpValidation, startExpModalValidation.revalidateIfNeeded]);

  /**
   * Снимок стейтов активного воркфлоу. 404 «нет активного воркфлоу» — не сбой:
   * на такой интеграции выбирать не из чего, поле просто не показывается.
   */
  const loadWorkflowStates = useCallback(async () => {
    if (!integrationId) return;
    setWorkflowStatesStatus('loading');
    try {
      const snapshot = await vuser.getWorkflowStates(integrationId);
      setWorkflowStates(snapshot);
      setWorkflowStatesStatus('ready');
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setWorkflowStates(null);
      setWorkflowStatesStatus(status === 404 ? 'absent' : 'error');
    }
  }, [integrationId]);

  useEffect(() => {
    if (!startExpOpen || !integrationId) return;
    void loadWorkflowStates();
  }, [startExpOpen, integrationId, loadWorkflowStates]);

  useEffect(() => {
    if (!startExpOpen || !integrationId) {
      setAssistantConfigEnvelope(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const resp = await connector.getIntegrationConfig(integrationId);
        if (!cancelled) {
          setAssistantConfigEnvelope(buildAssistantConfigPayloadFromIntegration(resp));
        }
      } catch {
        if (!cancelled) setAssistantConfigEnvelope(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startExpOpen, integrationId]);

  useEffect(() => {
    if (!startExpOpen || !assistantConfigEnvelope || !integrationId) {
      setTokenPreviewResult(null);
      setTokenPreviewError(null);
      setTokenPreviewLoading(false);
      return;
    }

    const dialog_count = Number(startExp.dialog_count);
    const message_count = Number(startExp.messages_per_dialog);
    if (
      !Number.isFinite(dialog_count) ||
      dialog_count < 0 ||
      !Number.isFinite(message_count) ||
      message_count < 0
    ) {
      return;
    }

    const userIds = startExp.useCustomPersonaPairs
      ? Array.from(new Set(startExp.userPersonaPairs.map((p) => p.virtual_user_id).filter(Boolean)))
      : startExp.virtual_user_ids.filter(Boolean);
    const adminIds = startExp.useCustomPersonaPairs
      ? Array.from(new Set(startExp.adminPersonaPairs.map((p) => p.virtual_user_id).filter(Boolean)))
      : startExp.virtual_admin_ids.filter(Boolean);

    const timer = window.setTimeout(() => {
      void (async () => {
        setTokenPreviewLoading(true);
        setTokenPreviewError(null);
        try {
          const body = buildDatasetGenerationPredictTokenUsageRequest({
            assistant_config: assistantConfigEnvelope,
            vuser_config: buildVuserConfigPayload(virtualUsers, userIds),
            vadmin_config: buildVuserConfigPayload(virtualUsers, adminIds),
            dialog_count,
            message_count,
          });
          const result = await vuser.predictDatasetGenerationTokenUsage(integrationId, body);
          setTokenPreviewResult(result);
        } catch (e: unknown) {
          setTokenPreviewResult(null);
          setTokenPreviewError(getErrorMessage(e, 'Token estimate failed'));
        } finally {
          setTokenPreviewLoading(false);
        }
      })();
    }, 500);

    return () => window.clearTimeout(timer);
  }, [startExpOpen, assistantConfigEnvelope, integrationId, startExp, virtualUsers]);

  const startExperimentDisabled =
    loading ||
    (!startExp.useCustomPersonaPairs && virtualUsersMissingPersona.length > 0) ||
    (startExp.useCustomPersonaPairs && startExp.userPersonaPairs.length === 0);

  const getDatasetTableCacheEntry = useCallback(
    (row: ConnectorDataset): DatasetContentTableCacheEntry | null => {
      const id = resolveDatasetId(row);
      return id ? datasetContentTableCache[id] ?? null : null;
    },
    [datasetContentTableCache, resolveDatasetId]
  );

  const getDatasetStatsForTableRow = useCallback(
    (row: ConnectorDataset): Record<string, unknown> | null => {
      return getDatasetTableCacheEntry(row)?.stats ?? getDatasetStatsFromRow(row);
    },
    [getDatasetTableCacheEntry]
  );

  const getDatasetWarningForTableRow = useCallback(
    (row: ConnectorDataset): DatasetDataWarning | null => {
      return getDatasetTableCacheEntry(row)?.warning ?? getDatasetWarningFromRow(row);
    },
    [getDatasetTableCacheEntry]
  );

  const connectorDatasetColumnKeys = useMemo(() => {
    const keys = new Set<string>();
    connectorDatasets.forEach((row) => {
      Object.keys(row as Record<string, unknown>).forEach((k) => {
        if (!HIDDEN_CONNECTOR_DATASET_COLUMNS.has(k)) keys.add(k);
      });
    });
    if (connectorDatasets.length > 0) keys.add('__stats_summary');
    if (connectorDatasets.some((row) => getDatasetWarningForTableRow(row))) keys.add('__warning');
    const preferred = [
      'id',
      'label',
      'inner_label',
      'name',
      '__warning',
      '__stats_summary',
      'created_at',
      'updated_at',
      'storage',
      's3_key',
    ];
    return Array.from(keys).filter((key) => !HIDDEN_CONNECTOR_DATASET_COLUMNS.has(key)).sort((a, b) => {
      const ia = preferred.indexOf(a);
      const ib = preferred.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [connectorDatasets, getDatasetWarningForTableRow]);

  const handleStartExperiment = async () => {
    if (!integrationId) return;
    if (startExpModalValidation.reportValidation(buildStartExpValidation())) return;

    const first_message = startExp.first_message.trim();
    const dialog_count = Number(startExp.dialog_count);
    const messages_per_dialog = Number(startExp.messages_per_dialog);
    const experiment_name = startExp.experiment_name.trim();
    
    const userPersonaPairs = rowsToBindings(startExp.userPersonaPairs).filter(
      (p) => p.virtual_user_id && p.persona_id
    );
    const adminPersonaPairs = rowsToBindings(startExp.adminPersonaPairs).filter(
      (p) => p.virtual_user_id && p.persona_id
    );

    // Convert percentages to ratios (0.0 to 1.0)
    const train_ratio = startExp.train_ratio / 100;
    const validation_ratio = startExp.validation_ratio / 100;

    const limit_history = startExp.limit_history;
    const history_depth = limit_history ? clampHistoryDepth(startExp.history_depth, DEFAULT_HISTORY_DEPTH) : null;
    const reaction_weights = buildReactionWeightsFromForm(startExp.reactionWeights);
    /**
     * Входной стейт не отправляем: пустое значение и так означает вход графа, а
     * явный id устарел бы, если граф поменяли между снимком и запуском.
     */
    const chosenEntryState = (startExp.entry_state_id || '').trim();
    const entry_state_id =
      chosenEntryState && chosenEntryState !== workflowStates?.entry_state_id ? chosenEntryState : '';
    
    setLoading(true);
    startExpModalValidation.clearErrors();
    setError(null);
    try {
      const commonBody = {
        experiment_name,
        first_message,
        dialog_count,
        messages_per_dialog,
        limit_history,
        history_depth,
        train_ratio,
        validation_ratio,
        ...(reaction_weights ? { reaction_weights } : {}),
        ...(entry_state_id ? { entry_state_id } : {}),
      };

      if (startExp.useCustomPersonaPairs) {
        await vuser.startExperiment(integrationId, {
          ...commonBody,
          virtual_user_personas: userPersonaPairs,
          ...(adminPersonaPairs.length ? { virtual_admin_personas: adminPersonaPairs } : {}),
        });
      } else {
        const vu = startExp.virtual_user_ids.filter(Boolean);
        const va = startExp.virtual_admin_ids.filter(Boolean);
        await vuser.startExperiment(integrationId, {
          ...commonBody,
          virtual_user_ids: vu,
          virtual_admin_ids: va.length ? va : [],
        });
      }

      setSuccess('Experiment started');
      clearSuccessSoon();
      setStartExpOpen(false);
      startExpModalValidation.clearErrors();
      setStartExp({ 
        experiment_name: '',
        useCustomPersonaPairs: false,
        virtual_user_ids: [], 
        virtual_admin_ids: [], 
        userPersonaPairs: [],
        adminPersonaPairs: [],
        first_message: DEFAULT_FIRST_MESSAGE,
        dialog_count: '10', 
        messages_per_dialog: '5',
        entry_state_id: '',
        limit_history: true,
        history_depth: String(DEFAULT_HISTORY_DEPTH),
        train_ratio: 75,
        validation_ratio: 25,
        reactionWeights: { ...EMPTY_REACTION_WEIGHTS_FORM },
      });
      await loadExperiments();
    } catch (e: any) {
      let errorMessage = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Failed to start experiment';
      errorMessage = errorMessage
        .replace(/dialog_count/gi, 'dialog count')
        .replace(/messages_per_dialog/gi, 'messages per dialog')
        .replace(/virtual_user_ids/gi, 'virtual user IDs')
        .replace(/virtual_admin_ids/gi, 'virtual admin IDs')
        .replace(/virtual_user_personas/gi, 'virtual user personas')
        .replace(/virtual_admin_personas/gi, 'virtual admin personas')
        .replace(/experiment_name/gi, 'dataset name')
        .replace(/history_depth/gi, 'history depth')
        .replace(/_/g, ' ')
        .trim();
      if (errorMessage.length > 0) {
        errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
      }
      /**
       * Граф могли поменять между тем, как форма получила снимок, и запуском.
       * Тогда приходит 400 про неизвестный стейт — перечитываем список, чтобы
       * пользователь выбирал из того, что есть сейчас, а не из устаревшего.
       */
      if (/is not a state of the active workflow/i.test(String(errorMessage))) {
        setStartExp((prev) => ({ ...prev, entry_state_id: '' }));
        void loadWorkflowStates();
        errorMessage =
          'The chosen start state is no longer part of the active workflow. The list has been reloaded — pick a state again.';
      }
      startExpModalValidation.reportApiError(errorMessage, START_EXP_ERROR_BANNER_ID);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelExperiment = async (id: string) => {
    if (!integrationId) return;
    setLoading(true);
    setError(null);
    try {
      await vuser.cancelExperiment(integrationId, id);
      setSuccess('Cancel requested');
      clearSuccessSoon();
      await loadExperiments();
    } catch (e: any) {
      setError(capitalizeError(e?.message) || 'Failed to cancel experiment');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryExperiment = async (id: string) => {
    if (!integrationId || retryingExperimentId) return;
    setRetryingExperimentId(id);
    setError(null);
    try {
      await vuser.retryExperiment(id, integrationId);
      setSuccess('New experiment queued. Refreshing list…');
      clearSuccessSoon();
      await loadExperiments({ silent: true });
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Failed to retry experiment'));
    } finally {
      setRetryingExperimentId(null);
    }
  };

  const openDeleteModal = (experiment: Experiment) => {
    // Backend should also block this, but we keep UI strict and safe.
    if (experiment.status === 'running' || experiment.status === 'queued') return;
    setDeleteModalError(null);
    setDeleteTarget(experiment);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (loading) return;
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setDeleteModalError(null);
  };

  const confirmDeleteExperiment = async () => {
    if (!integrationId) return;
    if (!deleteTarget) return;

    // Backend should also block this, but we keep UI strict and safe.
    if (deleteTarget.status === 'running' || deleteTarget.status === 'queued') return;

    setLoading(true);
    setError(null);
    try {
      await vuser.deleteExperiment(integrationId, deleteTarget.id);
      setSuccess('Dataset deleted');
      clearSuccessSoon();
      setDeleteModalError(null);
      closeDeleteModal();
      await loadExperiments();
    } catch (e: any) {
      const msg = getErrorMessage(e, 'Failed to delete dataset');
      setDeleteModalError(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Функция для вычисления delta score относительно предыдущего эксперимента
  const getScoreDelta = (currentExperiment: Experiment, allExperiments: Experiment[]): {
    delta: number | null;
    previousScore: number | null;
    isFirst: boolean;
  } => {
    if (typeof currentExperiment.score !== 'number') {
      return { delta: null, previousScore: null, isFirst: false };
    }

    // Сортируем все эксперименты по дате создания (от старых к новым)
    const sortedByDate = [...allExperiments]
      .filter(e => typeof e.score === 'number')
      .sort((a, b) => {
        const aDate = new Date(a.created_at).getTime();
        const bDate = new Date(b.created_at).getTime();
        return aDate - bDate;
      });

    // Находим индекс текущего эксперимента в отсортированном списке
    const currentIndex = sortedByDate.findIndex(e => e.id === currentExperiment.id);
    
    if (currentIndex <= 0) {
      // Это первый эксперимент или не найден
      return { delta: null, previousScore: null, isFirst: currentIndex === 0 };
    }

    // Берем предыдущий эксперимент
    const previousExperiment = sortedByDate[currentIndex - 1];
    const previousScore = previousExperiment.score;
    const delta = currentExperiment.score - previousScore;

    return { delta, previousScore, isFirst: false };
  };

  const handleScoreExperiment = async (id: string) => {
    if (!integrationId) return;

    // Save scroll position BEFORE opening modal to prevent scroll to top
    const scrollY = window.scrollY;
    
    setScoreModalOpen(true);
    setScoreModalLoading(true);
    setScoreModalError(null);
    setScoreModalExperiment(null);
    setScoreModalScore(null);
    setScoreModalStats(null);
    
    // Prevent scroll immediately - use setTimeout to ensure it happens after state update
    setTimeout(() => {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }, 0);

    try {
      const [expResp, scoreResp, statsResp, dialogsResp] = await Promise.all([
        vuser.getExperiment(integrationId, id),
        vuser.scoreExperiment(integrationId, id).catch(() => null),
        vuser.experimentStatistics(integrationId, id).catch(() => null),
        vuser.experimentDialogs(integrationId, id).catch(() => ({ data: [] })),
      ]);

      setScoreModalExperiment((expResp as any)?.data ?? (expResp as any));
      setScoreModalScore((scoreResp as any)?.data ?? (scoreResp as any));
      setScoreModalStats((statsResp as any)?.data ?? (statsResp as any));
      setScoreModalDialogs((dialogsResp as any)?.data ?? []);
    } catch (e: any) {
      setScoreModalError(capitalizeError(e?.message) || 'Failed to load experiment details');
    } finally {
      setScoreModalLoading(false);
    }
  };

  useEffect(() => {
    if (!openExperimentId || !integrationId) return;
    void handleScoreExperiment(openExperimentId);
    onOpenExperimentHandled?.();
  }, [openExperimentId, integrationId]);

  if (!integrationId) {
    return (
      <ConnectorSelectIntegrationPrompt
        description="Choose an integration to import data, export dialogs, and manage training datasets."
        onGoToIntegrations={() => navigate('/connector')}
      />
    );
  }

  return (
    <div
      className={`datasets-page-container${embedded ? ' datasets-page-container--embedded' : ''}`}
    >

      {error && !startExpOpen && !exportModalOpen && activeImportJob?.status !== 'failed' && (
        <div className="admin-message error" style={{ marginBottom: 16 }}>
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}
      {success && !exportModalOpen && (
        <div className="admin-message success" style={{ marginBottom: 16 }}>
          <i className="fas fa-check-circle"></i> {success}
        </div>
      )}

      {activeImportJob ? (
        <div
          className={`dataset-import-job-panel${
            activeImportJob.status === 'failed'
              ? ' dataset-import-job-panel--failed'
              : activeImportJob.status === 'completed'
                ? ' dataset-import-job-panel--completed'
                : activeImportJob.status === 'cancelled'
                  ? ' dataset-import-job-panel--cancelled'
                  : ''
          }`}
        >
          <div className="dataset-import-job-panel__head">
            <div>
              <h3 className="dataset-import-job-panel__title">Dataset import job</h3>
              <p className="dataset-import-job-panel__meta">
                {activeImportJob.dataset_label ? (
                  <>
                    <strong>{activeImportJob.dataset_label}</strong>
                    <span aria-hidden> · </span>
                  </>
                ) : null}
                <span title={activeImportJob.id}>ID {activeImportJob.id.slice(0, 8)}…</span>
                {activeImportJob.attempt ? <span> · attempt {activeImportJob.attempt}</span> : null}
              </p>
            </div>
            <span
              className={`dataset-import-job-panel__status dataset-import-job-panel__status--${activeImportJob.status}`}
            >
              {activeImportJobRunning
                ? formatDatasetImportJobPhase(activeImportJob.phase)
                : activeImportJob.status === 'completed'
                  ? 'Completed'
                  : activeImportJob.status === 'cancelled'
                    ? 'Cancelled'
                    : 'Failed'}
            </span>
          </div>

          {activeImportJobRunning ? (
            <DatasetImportJobProgress
              status={activeImportJob.status}
              phase={activeImportJob.phase}
              progressPercent={activeImportJob.progress_percent}
              displayPercent={importJobProgressDisplay}
            />
          ) : null}

          {activeImportJob.status === 'completed' ? (
            <>
              <p className="dataset-import-job-panel__message">{buildDatasetImportSuccessMessage(activeImportJob)}</p>
              <DatasetWarningBanner warning={activeImportJob.result?.warning} compact />
            </>
          ) : null}

          {activeImportJob.status === 'cancelled' ? (
            <p className="dataset-import-job-panel__message">Import cancelled.</p>
          ) : null}

          {activeImportJob.status === 'failed' ? (
            <p
              className="dataset-import-job-panel__message dataset-import-job-panel__message--error"
              role="alert"
            >
              {formatDatasetImportJobErrorMessage(activeImportJob)}
            </p>
          ) : null}

          <div className="dataset-import-job-panel__actions">
            {activeImportJobRunning && !exportModalOpen ? (
              <button type="button" className="btn-secondary" onClick={() => setExportModalOpen(true)}>
                Open import dialog
              </button>
            ) : null}
            {activeImportJobRunning ? (
              <button
                type="button"
                className="btn-secondary dataset-import-job-panel__cancel-btn"
                disabled={Boolean(importJobCancellingId)}
                aria-busy={importJobCancellingId === activeImportJob.id}
                onClick={() => void handleCancelDatasetImportJob()}
              >
                <i
                  className={`fas ${importJobCancellingId === activeImportJob.id ? 'fa-spinner fa-spin' : 'fa-ban'}`}
                  aria-hidden
                />{' '}
                {importJobCancellingId === activeImportJob.id ? 'Cancelling…' : 'Cancel import'}
              </button>
            ) : null}
            {!activeImportJobRunning ? (
              <button type="button" className="btn-secondary" onClick={dismissActiveImportJob}>
                Dismiss
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <ExperimentScoreModal
        isOpen={scoreModalOpen}
        onClose={() => setScoreModalOpen(false)}
        experiment={scoreModalExperiment}
        score={scoreModalScore}
        stats={scoreModalStats}
        dialogs={scoreModalDialogs}
        integrationId={integrationId}
        loading={scoreModalLoading}
        error={scoreModalError}
      />

      <DatasetsCollapsibleSection
        id="datasets-import-section"
        headId="datasets-import-header"
        open={importDatasetSectionOpen}
        onToggle={() => setImportDatasetSectionOpen((value) => !value)}
        title="Import dataset"
        subtitle={`${connectorDatasets.length} ${connectorDatasets.length === 1 ? 'dataset' : 'datasets'} in registry`}
        actions={
          <>
            <button
              type="button"
              className="action-btn refresh-btn"
              onClick={() => void loadConnectorDatasets()}
              disabled={connectorDatasetsLoading || !integrationId}
              style={{
                minWidth: '120px',
                padding: '10px 18px',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '8px',
              }}
            >
              <i className="fas fa-sync-alt" aria-hidden />
              <span className="btn-text">Refresh</span>
            </button>
            <button
              type="button"
              className="action-btn"
              onClick={openExportModal}
              disabled={!integrationId || activeImportJobRunning}
              style={{
                minWidth: '160px',
                padding: '10px 18px',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '8px',
                background: '#0099FA',
                color: '#FFFFFF',
                border: 'none',
              }}
              title="Import dataset (training export)"
            >
              <i className="fas fa-file-import" aria-hidden />
              <span className="btn-text">Import dataset</span>
            </button>
          </>
        }
      >
        <div className="datasets-table-wrap" style={{ overflowX: 'auto' }}>
          <table
            id="datasets-import-table"
            className="admin-table"
            style={{ minWidth: connectorDatasetColumnKeys.length ? undefined : 400 }}
          >
            <thead>
              <tr style={{ background: '#FFFFFF' }}>
                {connectorDatasetColumnKeys.map((key) => (
                  <th
                    key={key}
                    className={key === 'source' ? 'datasets-table-cell datasets-table-cell--source' : 'datasets-table-cell'}
                    style={{
                      padding: '14px 16px',
                      textAlign: 'left',
                      fontWeight: 700,
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: '#0A2A47',
                      borderBottom: '2px solid #E3F0FB',
                      whiteSpace: key === 'source' ? 'normal' : 'nowrap',
                    }}
                  >
                    {formatDatasetFieldLabel(key)}
                  </th>
                ))}
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: '#0A2A47',
                    borderBottom: '2px solid #E3F0FB',
                    whiteSpace: 'nowrap',
                    width: 160,
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {connectorDatasetsLoading && connectorDatasets.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(connectorDatasetColumnKeys.length + 1, 1)} style={{ padding: 24, color: '#5B7893' }}>
                    Loading…
                  </td>
                </tr>
              ) : connectorDatasets.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(connectorDatasetColumnKeys.length + 1, 1)} style={{ padding: 24, color: '#5B7893' }}>
                    No datasets yet. Import a dataset or wait for sync.
                  </td>
                </tr>
              ) : (
                connectorDatasets.map((row, idx) => (
                  <tr key={String((row as { id?: unknown }).id ?? idx)}>
                    {connectorDatasetColumnKeys.map((key) => {
                      const isSourceColumn = key === 'source';
                      const cacheEntry = getDatasetTableCacheEntry(row);
                      const warning = key === '__warning' ? getDatasetWarningForTableRow(row) : null;
                      const stats = key === '__stats_summary' ? getDatasetStatsForTableRow(row) : null;
                      const statsLoading = key === '__stats_summary' && cacheEntry?.loaded === false;
                      return (
                      <td
                        key={key}
                        className={
                          isSourceColumn
                            ? 'datasets-table-cell datasets-table-cell--source'
                            : 'datasets-table-cell'
                        }
                        style={{
                          padding: '12px 16px',
                          fontSize: 13,
                          borderBottom: '1px solid #E3F0FB',
                          verticalAlign: 'top',
                          ...(isSourceColumn ? {} : { maxWidth: 360, wordBreak: 'break-word' }),
                        }}
                      >
                        {key === '__warning' ? (
                          warning ? (
                            <span className="dataset-warning-badge" title={warning.message}>
                              <i className="fas fa-exclamation-triangle" aria-hidden />
                              Warning
                            </span>
                          ) : (
                            '—'
                          )
                        ) : key === '__stats_summary' ? (
                          <span className="dataset-stats-summary">
                            {statsLoading ? 'Loading…' : formatDatasetStatsSummary(stats)}
                          </span>
                        ) : (
                          formatConnectorCell((row as Record<string, unknown>)[key], key)
                        )}
                      </td>
                      );
                    })}
                    <td
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #E3F0FB',
                        verticalAlign: 'top',
                      }}
                    >
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={() => void openDatasetViewer(row)}
                        style={{
                          minWidth: 120,
                          height: 34,
                          borderRadius: 8,
                          border: 'none',
                          fontSize: 12,
                          fontWeight: 600,
                          background: '#0099FA',
                          color: '#FFFFFF',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                        }}
                      >
                        <i className="fas fa-eye" aria-hidden />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DatasetsCollapsibleSection>

      {datasetViewerOpen && (
        <div className="modal-overlay">
          <div
            className="modal-content modal-content--wide dataset-content-viewer"
          >
            <div className="modal-header">
              <h3>Dataset content viewer</h3>
              <button className="modal-close" onClick={closeDatasetViewer}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body dataset-content-viewer__body">
              {datasetViewerLoading ? <p style={{ margin: 0, color: '#4A6683' }}>Loading dataset content…</p> : null}
              {datasetViewerError ? (
                <div className="admin-message error" style={{ margin: '0 0 12px 0' }}>
                  <i className="fas fa-exclamation-circle"></i> {datasetViewerError}
                </div>
              ) : null}
              {!datasetViewerLoading && !datasetViewerError && datasetViewerData?.meta ? (
                <>
                  {(() => {
                    const meta = datasetViewerData.meta;
                    const metadata = parseDatasetMetadata(meta.metadata);
                    const metadataStats =
                      metadata?.stats && typeof metadata.stats === 'object'
                        ? (metadata.stats as Record<string, unknown>)
                        : null;
                    const stats = datasetViewerData.stats ?? metadataStats ?? null;
                    const innerLabel = meta.inner_label ?? metadata?.inner_label;
                    const source = String(meta.source || metadata?.source || '').toLowerCase();
                    const metadataPanelTitle = source === 'integration' ? 'Integration metadata' : 'Metadata';
                    const metadataPanelItems =
                      metadata && source === 'integration'
                        ? buildIntegrationDatasetMetadataItems(metadata)
                        : metadata && Object.keys(metadata).length > 0 && source !== 'vuser'
                          ? buildDatasetMetadataItems(metadata)
                          : [];

                    const visibleDialogs = stats ? readFiniteDatasetNumber(stats.visible_dialogs) : null;
                    const invisibleDialogs = stats ? readFiniteDatasetNumber(stats.invisible_dialogs) : null;
                    const dialogs =
                      stats && readFiniteDatasetNumber(stats.dialogs) !== null
                        ? readFiniteDatasetNumber(stats.dialogs)
                        : visibleDialogs !== null || invisibleDialogs !== null
                          ? (visibleDialogs ?? 0) + (invisibleDialogs ?? 0)
                          : null;
                    const messages = stats ? readFiniteDatasetNumber(stats.messages ?? stats.total_rows) : null;
                    const signalStatsValue = stats ? (
                      <>
                        dialogs {formatDatasetValue(dialogs)}, messages {formatDatasetValue(messages)}
                        {' · '}
                        likes {formatDatasetValue(stats.likes)}, dislikes {formatDatasetValue(stats.dislikes)}
                        {' · '}
                        comments {formatDatasetValue(stats.comments)}, ground truth {formatDatasetValue(stats.groundtruth)}
                      </>
                    ) : (
                      'dialogs —, messages —, likes —, dislikes —, comments —, ground truth —'
                    );

                    const overviewItems: Array<{ label: string; value: React.ReactNode }> = [
                      { label: formatDatasetFieldLabel('label'), value: meta.label || '—' },
                      { label: formatDatasetFieldLabel('source'), value: formatDatasetValue(meta.source) },
                      {
                        label: formatDatasetFieldLabel('created_at'),
                        value: formatDatasetDateTime(meta.created_at),
                      },
                      { label: formatDatasetFieldLabel('train_rows'), value: formatDatasetValue(stats?.train_rows) },
                      {
                        label: formatDatasetFieldLabel('validation_rows'),
                        value: formatDatasetValue(stats?.validation_rows),
                      },
                      { label: formatDatasetFieldLabel('test_rows'), value: formatDatasetValue(stats?.test_rows) },
                      { label: formatDatasetFieldLabel('total_rows'), value: formatDatasetValue(stats?.total_rows) },
                      { label: 'Signals', value: signalStatsValue },
                    ];
                    if (innerLabel) {
                      overviewItems.push({
                        label: formatDatasetFieldLabel('inner_label'),
                        value: formatDatasetValue(innerLabel),
                      });
                    }
                    if (metadataStats && typeof metadataStats.dataset_tokens_original === 'number') {
                      overviewItems.push({
                        label: formatDatasetFieldLabel('dataset_tokens_original'),
                        value: Number(metadataStats.dataset_tokens_original).toLocaleString(),
                      });
                    }

                    return (
                  <div className="dataset-content-viewer__meta">
                    <DatasetMetaPanel title="Overview" items={overviewItems} />
                    {metadataPanelItems.length > 0 ? (
                      <DatasetMetaPanel title={metadataPanelTitle} items={metadataPanelItems} />
                    ) : null}
                  </div>
                    );
                  })()}
                  {datasetViewerData.content_available ? (
                    <div className="dataset-content-viewer__splits">
                      {renderDatasetSplit('train', datasetViewerData.content?.['train'])}
                      {renderDatasetSplit('validation', datasetViewerData.content?.['validation'])}
                      {renderDatasetSplit('test', datasetViewerData.content?.['test'])}
                    </div>
                  ) : (
                    <div className="admin-message" style={{ margin: 0 }}>
                      <i className="fas fa-info-circle"></i>{' '}
                      {datasetViewerData.unavailable_reason || 'Dataset content is not available for viewing.'}
                    </div>
                  )}
                </>
              ) : null}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeDatasetViewer}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Virtual User experiments table */}
      <DatasetsCollapsibleSection
        id="datasets-vuser-section"
        headId="datasets-header"
        open={vuserDatasetSectionOpen}
        onToggle={() => setVuserDatasetSectionOpen((value) => !value)}
        title={embedded ? 'Virtual user experiments' : 'VUser dataset'}
        subtitle={`${experiments.length} ${experiments.length === 1 ? 'experiment' : 'experiments'} total`}
        actions={
          <>
            {onOpenOnboarding && (
              <button
                onClick={onOpenOnboarding}
                className="action-btn"
                style={{
                  background: '#0099FA',
                  color: '#FFFFFF',
                  minWidth: '100px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                }}
                title="Start guided tour"
              >
                <i className="fas fa-question-circle" aria-hidden="true"></i>
                <span className="btn-text">Help</span>
              </button>
            )}
            <button
              className="action-btn refresh-btn"
              onClick={() => void loadExperiments()}
              disabled={loading}
              style={{
                minWidth: '130px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '8px',
                transition: 'all 0.2s ease',
              }}
            >
              <i className="fas fa-sync-alt" aria-hidden="true"></i>
              <span className="btn-text">Refresh</span>
            </button>
            <button
              id="datasets-start-new-btn"
              className="action-btn apply-btn"
              onClick={() => {
                void loadVirtualUsers();
                startExpModalValidation.clearErrors();
                setStartExp({
                  experiment_name: '',
                  useCustomPersonaPairs: false,
                  virtual_user_ids: [],
                  virtual_admin_ids: [],
                  userPersonaPairs: [],
                  adminPersonaPairs: [],
                  first_message: DEFAULT_FIRST_MESSAGE,
                  dialog_count: '10',
                  messages_per_dialog: '5',
                  entry_state_id: '',
                  limit_history: true,
                  history_depth: String(DEFAULT_HISTORY_DEPTH),
                  train_ratio: 75,
                  validation_ratio: 25,
                  reactionWeights: { ...EMPTY_REACTION_WEIGHTS_FORM },
                });
                setStartExpOpen(true);
              }}
              disabled={loading}
              style={{
                minWidth: '140px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '8px',
                background: '#FF8300',
                boxShadow: '0 0 0 1px #E3F0FB',
                transition: 'all 0.2s ease',
              }}
            >
              <i className="fas fa-plus" aria-hidden="true"></i>
              <span className="btn-text">Start New</span>
            </button>
          </>
        }
      >
        <div className="datasets-table-wrap">
          <table id="datasets-table" className="admin-table">
            <thead>
              <tr style={{ background: '#FFFFFF' }}>
                <th style={{ 
                  padding: '18px 24px', 
                  textAlign: 'left', 
                  fontWeight: 700, 
                  fontSize: '12px',
                  color: '#0A2A47',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  borderBottom: '2px solid #E3F0FB',
                  maxWidth: 240,
                }}>Name</th>
                <th style={{ 
                  padding: '18px 24px', 
                  textAlign: 'left', 
                  fontWeight: 700, 
                  fontSize: '12px',
                  color: '#0A2A47',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  borderBottom: '2px solid #E3F0FB',
                }}>Start Date</th>
                <th style={{ 
                  padding: '18px 24px', 
                  textAlign: 'left', 
                  fontWeight: 700, 
                  fontSize: '12px',
                  color: '#0A2A47',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  borderBottom: '2px solid #E3F0FB',
                }}>Status</th>
                <th style={{ 
                  padding: '18px 24px', 
                  textAlign: 'left', 
                  fontWeight: 700, 
                  fontSize: '12px',
                  color: '#0A2A47',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  borderBottom: '2px solid #E3F0FB',
                  minWidth: 140,
                }}>Progress</th>
                <th style={{ 
                  padding: '18px 24px', 
                  textAlign: 'left', 
                  fontWeight: 700, 
                  fontSize: '12px',
                  color: '#0A2A47',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  borderBottom: '2px solid #E3F0FB',
                }}>Score</th>
                <th style={{ 
                  padding: '18px 24px', 
                  textAlign: 'right', 
                  fontWeight: 700, 
                  fontSize: '12px',
                  color: '#0A2A47',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  borderBottom: '2px solid #E3F0FB',
                  minWidth: 120,
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && experiments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    <div className="admin-loading" style={{ padding: 24 }}>
                      <div className="spinner spinner--small"></div>
                      <p>Loading datasets…</p>
                    </div>
                  </td>
                </tr>
              ) : experiments.length === 0 && !error ? (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    <div className="empty-state">
                      <div className="empty-title">No datasets</div>
                      <div className="empty-subtitle">Start a new experiment to create a dataset.</div>
                    </div>
                  </td>
                </tr>
              ) : experiments.length === 0 && error ? (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    <div className="empty-state">
                      <div className="empty-title">Could not load datasets</div>
                      <div className="empty-subtitle">See the message above and try refreshing the page after signing in.</div>
                    </div>
                  </td>
                </tr>
              ) : (
                experiments.map((ex, index) => (
                  <tr 
                    key={ex.id} 
                    title={`ID: ${ex.id}`}
                    style={{
                      borderBottom: index < experiments.length - 1 ? '1px solid #E3F0FB' : 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#FFFFFF';
                    }}
                  >
                    <td data-label="Name" style={{ padding: '20px 24px', fontSize: '14px', color: '#0A2A47', maxWidth: '300px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        <i className="fas fa-tag" style={{ color: 'var(--brand-primary, #FF8300)', fontSize: '13px', flexShrink: 0 }}></i>
                        <span 
                          style={{ 
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: 1
                          }}
                          title={ex.name || ex.id}
                        >
                          {ex.name || ex.id.substring(0, 8) + '...'}
                        </span>
                      </div>
                    </td>
                    <td data-label="Start Date" style={{ padding: '20px 24px', fontSize: '14px', color: '#0A2A47' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="fas fa-calendar-alt" style={{ color: '#0099FA', fontSize: '13px' }}></i>
                        <span style={{ fontWeight: 500 }}>{formatStartDate(ex.started_at)}</span>
                      </div>
                    </td>
                    <td data-label="Status" style={{ padding: '20px 24px' }}>
                      <ExperimentStatusBadge experiment={ex} showWarningMessage />
                    </td>
                    <td data-label="Progress" style={{ padding: '20px 24px' }}>
                      {(() => {
                        const progress = getExperimentProgressMetrics(ex);
                        const display = resolveExperimentDisplayState(ex);
                        const subLabel = [progress.subLabel, display.progressNote]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <ProgressBar
                            inline
                            completed={progress.completed}
                            total={progress.total}
                            failed={progress.failed}
                            subLabel={subLabel || undefined}
                            variant={display.progressVariant}
                            title={
                              progress.failed > 0
                                ? `${progress.failed} message(s) failed`
                                : display.progressNote || undefined
                            }
                          />
                        );
                      })()}
                    </td>
                    <td data-label="Score" style={{ padding: '20px 24px' }}>
                      {typeof ex.score === 'number' ? (() => {
                        const display = resolveExperimentDisplayState(ex);
                        const { delta, isFirst } = getScoreDelta(ex, experiments);
                        const hasDelta = delta !== null && !isFirst;
                        const deltaValue = delta ?? 0;
                        const isIncrease = deltaValue > 0.001; // Учитываем погрешность округления
                        const isDecrease = deltaValue < -0.001;
                        const isNoChange = !isIncrease && !isDecrease && hasDelta;

                        return (
                          <div 
                            role="button"
                            tabIndex={0}
                            onClick={() => void handleScoreExperiment(ex.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void handleScoreExperiment(ex.id);
                              }
                            }}
                            title="Score details"
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              flexWrap: 'wrap',
                              cursor: 'pointer',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              backgroundColor: 'transparent',
                              border: '1px solid transparent',
                              position: 'relative',
                              touchAction: 'manipulation',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            <span style={{ 
                              fontWeight: 700, 
                              fontSize: '16px',
                              color: '#0A2A47'
                            }}>
                              {ex.score.toFixed(2)}
                            </span>
                            {display.scoreCaption ? (
                              <span className="experiment-score-caption">{display.scoreCaption}</span>
                            ) : null}
                            {hasDelta && (
                              <span style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: isIncrease ? '#0099FA' : isDecrease ? '#FF8300' : '#5B7893',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                backgroundColor: isIncrease ? 'rgba(0, 153, 250, 0.1)' : isDecrease ? 'rgba(255, 131, 0, 0.1)' : 'rgba(91, 120, 147, 0.1)'
                              }}>
                                ({deltaValue >= 0 ? '+' : ''}{deltaValue.toFixed(2)})
                                {isIncrease && (
                                  <i className="fas fa-arrow-up" style={{ fontSize: '11px', color: '#0099FA' }}></i>
                                )}
                                {isDecrease && (
                                  <i className="fas fa-arrow-down" style={{ fontSize: '11px', color: '#FF8300' }}></i>
                                )}
                                {isNoChange && (
                                  <span style={{ fontSize: '11px', color: '#5B7893' }}>0%</span>
                                )}
                              </span>
                            )}
                            <i 
                              className="fas fa-external-link-alt" 
                              style={{ 
                                fontSize: '11px', 
                                color: '#0099FA',
                                opacity: 0.6,
                                marginLeft: '4px',
                                flexShrink: 0
                              }}
                              aria-hidden
                            ></i>
                          </div>
                        );
                      })() : (
                        <span style={{ color: '#7F9AB3', fontSize: '14px' }}>N/A</span>
                      )}
                    </td>
                    <td className="actions-cell" data-label="Actions" style={{ padding: '20px 24px', textAlign: 'right' }}>
                      <div className="actions-wrap" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button 
                          className="edit-btn" 
                          onClick={() => void handleScoreExperiment(ex.id)} 
                          disabled={loading} 
                          title={`Dataset ID: ${ex.id}`}
                          style={{
                            ...DATASET_ACTION_BTN_BASE,
                            background: '#0099FA',
                            color: '#FFFFFF',
                            boxShadow: '0 0 0 1px #E3F0FB',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 153, 250, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 153, 250, 0.3)';
                          }}
                        >
                          <i className="fas fa-info-circle"></i> Details
                        </button>
                        {(ex.status === 'running' || ex.status === 'queued') && (
                          <button 
                            className="edit-btn" 
                            onClick={() => void handleCancelExperiment(ex.id)} 
                            disabled={loading}
                            title={ex.status === 'queued' ? 'Cancel queued dataset generation' : 'Cancel running dataset generation'}
                            style={{ 
                              ...DATASET_ACTION_BTN_BASE,
                              color: '#FFFFFF',
                              background: '#FF8300',
                              boxShadow: '0 0 0 1px #E3F0FB',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 131, 0, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 131, 0, 0.3)';
                            }}
                          >
                            <i className="fas fa-ban"></i> Cancel
                          </button>
                        )}
                        {canRetryExperiment(ex.status) && (
                          <button
                            type="button"
                            className="edit-btn"
                            onClick={() => void handleRetryExperiment(ex.id)}
                            disabled={loading || retryingExperimentId === ex.id}
                            title="Start a new experiment from this one"
                            style={{
                              ...DATASET_ACTION_BTN_BASE,
                              color: '#FFFFFF',
                              background: '#5B7893',
                              boxShadow: '0 0 0 1px #E3F0FB',
                              opacity: retryingExperimentId === ex.id ? 0.7 : 1,
                            }}
                          >
                            <i
                              className={`fas ${retryingExperimentId === ex.id ? 'fa-spinner fa-spin' : 'fa-redo'}`}
                              aria-hidden
                            />
                            {retryingExperimentId === ex.id ? 'Retrying…' : 'Retry'}
                          </button>
                        )}
                        <button
                          className="edit-btn"
                          onClick={() => openDeleteModal(ex)}
                          disabled={loading || ex.status === 'running' || ex.status === 'queued'}
                          title={
                            ex.status === 'running' || ex.status === 'queued'
                              ? `Deletion is disabled for ${ex.status} datasets`
                              : `Delete dataset (ID: ${ex.id})`
                          }
                          style={{
                            ...DATASET_ACTION_BTN_BASE,
                            color: '#FFFFFF',
                            background: '#FF8300',
                            boxShadow: '0 0 0 1px #E3F0FB',
                            opacity: loading || ex.status === 'running' || ex.status === 'queued' ? 0.55 : 1,
                            cursor: loading || ex.status === 'running' || ex.status === 'queued' ? 'not-allowed' : 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            if ((e.currentTarget as HTMLButtonElement).disabled) return;
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 131, 0, 0.35)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 131, 0, 0.25)';
                          }}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DatasetsCollapsibleSection>

      {/* Delete Dataset Confirmation Modal */}
      {deleteModalOpen && deleteTarget && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Delete Dataset</h3>
              <button className="modal-close" onClick={closeDeleteModal} disabled={loading}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {deleteModalError && (
                <div className="admin-message error" style={{ margin: '0 0 16px 0' }}>
                  <i className="fas fa-exclamation-circle"></i> {deleteModalError}
                </div>
              )}

              <div className="admin-message" style={{ 
                margin: 0, 
                border: '1px solid #FF8300',
                borderTop: '1px solid #FF8300',
                borderRight: '1px solid #FF8300',
                borderBottom: '1px solid #FF8300',
                borderLeft: '4px solid #FF8300',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 131, 0, 0.08)',
                boxShadow: '0 0 0 1px #E3F0FB'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#FF8300' }}></i>{' '}
                This will permanently delete the dataset and all related dialogs. This action cannot be undone.
              </div>

              <div style={{ marginTop: 16, fontSize: 14, color: '#0A2A47', lineHeight: 1.6 }}>
                <div>
                  <strong>Dataset:</strong>{' '}
                  {(deleteTarget.name || '').trim() || `${deleteTarget.id.substring(0, 8)}...`}
                </div>
                <div style={{ color: '#5B7893', marginTop: 6 }}>
                  <strong>ID:</strong> {deleteTarget.id}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeDeleteModal} disabled={loading}>
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={() => void confirmDeleteExperiment()}
                disabled={loading}
                style={{ background: '#FF8300' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Dataset Modal (POST /training/datasets/import-jobs) */}
      {exportModalOpen &&
        renderEmbeddedOverlay(
        <div className="connector-form-modal export-dataset-modal" style={{ zIndex: 10050 }}>
          <div className="export-dataset-modal-content">
            <div className="export-dataset-modal-header">
              <h3>
                <i className="fas fa-file-import"></i>
                Import Dataset
              </h3>
              <button type="button" className="export-dataset-modal-close" onClick={closeExportModal} aria-label="Close">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="export-dataset-modal-body" ref={exportModalBodyRef}>
              {exportModalValidation.bannerError && (
                <div
                  id={EXPORT_ERROR_BANNER_ID}
                  className="export-dataset-message export-dataset-message-error"
                  role="alert"
                  aria-live="polite"
                >
                  <i className="fas fa-exclamation-circle" aria-hidden />
                  <span>{exportModalValidation.bannerError}</span>
                </div>
              )}
              {exportSuccess && (
                <div className="export-dataset-message export-dataset-message-success">
                  <i className="fas fa-check-circle"></i>
                  {exportSuccess}
                </div>
              )}
              {activeImportJob && activeImportJobRunning ? (
                <div className="export-dataset-form-section export-dataset-import-progress">
                  <DatasetImportJobProgress
                    status={activeImportJob.status}
                    phase={activeImportJob.phase}
                    progressPercent={activeImportJob.progress_percent}
                    displayPercent={importJobProgressDisplay}
                  />
                  <p className="export-dataset-section-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                    {[
                      activeImportJob.attempt ? `Attempt ${activeImportJob.attempt}` : null,
                      'You can close this dialog — progress stays visible on the Datasets page while the job runs.',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn-secondary dataset-import-job-panel__cancel-btn"
                      disabled={Boolean(importJobCancellingId)}
                      aria-busy={importJobCancellingId === activeImportJob.id}
                      onClick={() => void handleCancelDatasetImportJob()}
                    >
                      <i
                        className={`fas ${importJobCancellingId === activeImportJob.id ? 'fa-spinner fa-spin' : 'fa-ban'}`}
                        aria-hidden
                      />{' '}
                      {importJobCancellingId === activeImportJob.id ? 'Cancelling…' : 'Cancel import'}
                    </button>
                  </div>
                </div>
              ) : null}
              {activeImportJob?.status === 'cancelled' ? (
                <div className="export-dataset-form-section">
                  <p className="export-dataset-section-hint" style={{ margin: 0 }}>
                    Import was cancelled.
                  </p>
                </div>
              ) : null}
              {activeImportJob?.status === 'failed' ? (
                <div className="export-dataset-form-section">
                  <p className="export-dataset-section-hint" style={{ margin: 0 }}>
                    Import failed. Review the message in the import job panel on this page, adjust settings, and try
                    again.
                  </p>
                </div>
              ) : null}
              {activeImportJob?.status === 'completed' && activeImportJob.result?.warning ? (
                <DatasetWarningBanner warning={activeImportJob.result.warning} />
              ) : null}
              <p className="export-dataset-description">
                Import from Virtual Psychologist into AI Tuner: pick a label, set the time window (used for source lists
                and as <code>training_period</code> on import), optionally narrow by users and dialogs, choose signals,
                then Preview tokens and Import. Token preview is an estimate and does not guarantee a successful import.
              </p>
              <ModalFormField
                id={EXPORT_DATASET_NAME_ID}
                label="Dataset"
                required
                error={exportModalValidation.fieldErrors[EXPORT_DATASET_NAME_ID]}
                className="export-dataset-form-section"
              >
                <input
                  id={EXPORT_DATASET_NAME_ID}
                  type="text"
                  value={exportForm.dataset_name}
                  onChange={(e) => setExportForm((f) => ({ ...f, dataset_name: e.target.value }))}
                  className={`export-dataset-input${exportModalValidation.fieldErrors[EXPORT_DATASET_NAME_ID] ? ' export-dataset-input--invalid' : ''}`}
                  placeholder="Dataset label (as returned for this integration)"
                  aria-invalid={Boolean(exportModalValidation.fieldErrors[EXPORT_DATASET_NAME_ID])}
                  aria-describedby={
                    exportModalValidation.fieldErrors[EXPORT_DATASET_NAME_ID]
                      ? `${EXPORT_DATASET_NAME_ID}-error`
                      : undefined
                  }
                />
              </ModalFormField>
              <div
                className={`export-dataset-form-section${exportModalValidation.fieldErrors[EXPORT_PERIOD_SECTION_ID] ? ' export-dataset-form-section--invalid' : ''}`}
                id={EXPORT_PERIOD_SECTION_ID}
              >
                <label className="export-dataset-label">Training period</label>
                {exportModalValidation.fieldErrors[EXPORT_PERIOD_SECTION_ID] ? (
                  <p className="modal-form-field__error" role="alert">
                    {exportModalValidation.fieldErrors[EXPORT_PERIOD_SECTION_ID]}
                  </p>
                ) : null}
                <p className="export-dataset-section-hint">
                  Select the time range for the dataset. Only users and dialogs from this period will be included in
                  the export.
                </p>
                <div className="export-dataset-period-row">
                  <div className="export-dataset-period-field">
                    <DateTimePicker
                      label="From"
                      value={exportForm.from}
                      onChange={(v) => setExportForm((f) => ({ ...f, from: v }))}
                      max={exportForm.to || undefined}
                    />
                  </div>
                  <div className="export-dataset-period-field">
                    <DateTimePicker
                      label="To"
                      value={exportForm.to}
                      onChange={(v) => setExportForm((f) => ({ ...f, to: v }))}
                      min={exportForm.from || undefined}
                    />
                  </div>
                </div>
              </div>
              <div
                className={`export-dataset-form-section${
                  exportModalValidation.fieldErrors[EXPORT_USERS_SECTION_ID]
                    ? ' export-dataset-form-section--invalid modal-form-field--invalid'
                    : ''
                }`}
                id={EXPORT_USERS_SECTION_ID}
              >
                <label className="export-dataset-label">Source users</label>
                <p className="export-dataset-section-hint">
                  Only users who consented to training use are listed. All eligible users are selected by default;
                  narrow the list to load dialogs for specific users only.
                </p>
                {exportModalValidation.fieldErrors[EXPORT_USERS_SECTION_ID] ? (
                  <p className="modal-form-field__error" role="alert">
                    {exportModalValidation.fieldErrors[EXPORT_USERS_SECTION_ID]}
                  </p>
                ) : null}
                <div
                  className={`export-dataset-picker-summary${
                    exportSelectedUserIds.length > 0 ? ' export-dataset-picker-summary--active' : ''
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {exportSelectedUserIds.length > 0 && (
                    <span className="export-dataset-picker-summary__badge">
                      {exportSelectedUserIds.length === 1 ? '1 user' : `${exportSelectedUserIds.length} users`}
                    </span>
                  )}
                  <span>{formatExportUserSelectionSummary(exportSelectedUserIds.length)}</span>
                </div>
                <div className="export-dataset-picker-toolbar">
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={exportLoading || exportSourceUsersLoading || exportSourceUsers.length === 0}
                    onClick={selectAllExportUsers}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={exportLoading || exportSelectedUserIds.length === 0}
                    onClick={deselectAllExportUsers}
                  >
                    Deselect all
                  </button>
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={exportLoading}
                    onClick={() => void loadExportSourceUsers()}
                  >
                    Refresh users
                  </button>
                </div>
                {exportSourceUsersLoading ? (
                  <p className="export-dataset-picker-empty">Loading users…</p>
                ) : exportSourceUsers.length === 0 ? (
                  <p className="export-dataset-picker-empty">No eligible users in this period.</p>
                ) : (
                  <div className="export-dataset-picker-list" role="listbox" aria-multiselectable="true">
                    {exportSourceUsers.map((u) => {
                      const id = (u.user_id || '').trim();
                      if (!id) return null;
                      const checked = exportSelectedUserIds.includes(id);
                      const label = u.user_email || id;
                      return (
                        <label
                          key={id}
                          className={`export-dataset-picker-item${checked ? ' export-dataset-picker-item--selected' : ''}`}
                          aria-selected={checked}
                        >
                          <input
                            type="checkbox"
                            className="export-dataset-picker-item__input"
                            checked={checked}
                            disabled={exportLoading}
                            onChange={() => {
                              setExportSelectedUserIds((prev) =>
                                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                              );
                            }}
                          />
                          <span className="export-dataset-picker-item__check" aria-hidden="true" />
                          <span className="export-dataset-picker-item__content">
                            <span className="export-dataset-picker-item__primary">
                              {label}
                              {checked ? <span className="export-dataset-picker-item__selected-tag">Selected</span> : null}
                            </span>
                            <span className="export-dataset-picker-item__meta">
                              Dialogs: {u.dialogs_count ?? '—'} · Messages: {u.messages_count ?? '—'}
                              {u.last_message_at ? ` · Last: ${formatDatasetDateTime(u.last_message_at)}` : ''}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div
                className={`export-dataset-form-section${
                  exportModalValidation.fieldErrors[EXPORT_DIALOGS_SECTION_ID]
                    ? ' export-dataset-form-section--invalid modal-form-field--invalid'
                    : ''
                }`}
                id={EXPORT_DIALOGS_SECTION_ID}
              >
                <label className="export-dataset-label">Source dialogs</label>
                <p className="export-dataset-section-hint">
                  Conversations to import. All dialogs in scope are selected automatically after the list loads.{' '}
                  <code>dialog_id</code> matches VP <code>chat_id</code>. Changing users reloads the list and
                  re-selects all dialogs.
                </p>
                {exportModalValidation.fieldErrors[EXPORT_DIALOGS_SECTION_ID] ? (
                  <p className="modal-form-field__error" role="alert">
                    {exportModalValidation.fieldErrors[EXPORT_DIALOGS_SECTION_ID]}
                  </p>
                ) : null}
                <div
                  className={`export-dataset-picker-summary${
                    exportSelectedDialogIds.length > 0 ? ' export-dataset-picker-summary--active' : ''
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {exportSelectedDialogIds.length > 0 && (
                    <span className="export-dataset-picker-summary__badge">
                      {exportSelectedDialogIds.length === 1
                        ? '1 dialog'
                        : `${exportSelectedDialogIds.length} dialogs`}
                    </span>
                  )}
                  <span>
                    {formatExportDialogSelectionSummary(
                      exportSelectedDialogIds.length,
                      exportSelectedDialogsOnPageCount
                    )}
                  </span>
                </div>
                <div className="export-dataset-picker-toolbar">
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={
                      exportLoading ||
                      exportSourceDialogsLoading ||
                      exportSelectAllDialogsBusy ||
                      exportSourceDialogs.length === 0
                    }
                    onClick={selectExportDialogsOnPage}
                  >
                    Select page
                  </button>
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={
                      exportLoading ||
                      exportSourceDialogsLoading ||
                      exportSelectAllDialogsBusy ||
                      exportDialogsMeta.total === 0
                    }
                    onClick={() => void selectAllExportDialogsInScope()}
                  >
                    {exportSelectAllDialogsBusy ? 'Selecting…' : 'Select all in scope'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={exportLoading || exportSelectedDialogIds.length === 0}
                    onClick={deselectAllExportDialogs}
                  >
                    Deselect all
                  </button>
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={exportLoading || exportSelectAllDialogsBusy}
                    onClick={() => void loadExportSourceDialogs()}
                  >
                    Refresh page
                  </button>
                  <span className="export-dataset-picker-toolbar__meta">
                    Page {exportDialogsPage} / {exportDialogsMeta.total_pages} · {exportDialogsMeta.total} dialogs
                  </span>
                </div>
                <div className="export-dataset-picker-pagination">
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={exportLoading || exportDialogsPage <= 1}
                    onClick={() => setExportDialogsPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="btn-secondary export-dataset-picker-toolbar__btn"
                    disabled={exportLoading || exportDialogsPage >= exportDialogsMeta.total_pages}
                    onClick={() => setExportDialogsPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
                {exportSourceDialogsLoading ? (
                  <p className="export-dataset-picker-empty">Loading dialogs…</p>
                ) : exportSourceDialogs.length === 0 ? (
                  <p className="export-dataset-picker-empty">No dialogs for this page and filters.</p>
                ) : (
                  <div className="export-dataset-dialog-table-wrap">
                    <table className="export-dataset-dialog-table">
                      <thead>
                        <tr>
                          <th aria-label="Select dialog" />
                          <th>Title</th>
                          <th>User</th>
                          <th title="Total messages in the dialog">Messages</th>
                          <th title="AI (assistant) messages in the dialog">AI</th>
                          <th title="Messages with a reaction (like / dislike / comment / ground truth)">
                            Reactions
                          </th>
                          <th>Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exportSourceDialogs.map((d) => {
                          const id = (d.dialog_id || '').trim();
                          if (!id) return null;
                          const checked = exportSelectedDialogIds.includes(id);
                          return (
                            <tr
                              key={id}
                              className={checked ? 'export-dataset-dialog-row--selected' : undefined}
                              aria-selected={checked}
                            >
                              <td>
                                <label className="export-dataset-dialog-row-check">
                                <input
                                  type="checkbox"
                                    className="export-dataset-picker-item__input"
                                  checked={checked}
                                  disabled={exportLoading}
                                    aria-label={checked ? `Deselect ${d.title?.trim() || UNTITLED_DIALOG_LABEL}` : `Select ${d.title?.trim() || UNTITLED_DIALOG_LABEL}`}
                                  onChange={() => {
                                    setExportSelectedDialogIds((prev) =>
                                      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                                    );
                                  }}
                                />
                                  <span className="export-dataset-picker-item__check" aria-hidden="true" />
                                </label>
                              </td>
                              <td>
                                <ExportSourceDialogTitle title={d.title} />
                                <div className="export-dataset-dialog-id">{id}</div>
                                {checked ? (
                                  <span className="export-dataset-picker-item__selected-tag export-dataset-picker-item__selected-tag--inline">
                                    Selected
                                  </span>
                                ) : null}
                              </td>
                              <td>{d.user_email || d.user_id || '—'}</td>
                              <td className="export-dataset-dialog-count">
                                {typeof d.messages_count === 'number' ? d.messages_count : '—'}
                              </td>
                              <td className="export-dataset-dialog-count">
                                {typeof d.ai_messages_count === 'number' ? d.ai_messages_count : '—'}
                              </td>
                              <td className="export-dataset-dialog-count">
                                {typeof d.reactions_count === 'number' ? (
                                  <span
                                    className={`export-dataset-dialog-reactions${
                                      d.reactions_count > 0
                                        ? ' export-dataset-dialog-reactions--has'
                                        : ''
                                    }`}
                                  >
                                    <i className="fas fa-thumbs-up" aria-hidden="true" />
                                    {d.reactions_count}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="export-dataset-dialog-updated">
                                {d.updated_at ? formatDatasetDateTime(d.updated_at) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="export-dataset-form-section">
                <label className="export-dataset-label">Train / Validation / Test ratio</label>
                <TrainValTestRatioSlider
                  idPrefix="export-dataset"
                  train={exportForm.train_ratio}
                  validation={exportForm.validation_ratio}
                  test={exportForm.test_ratio}
                  disabled={exportLoading}
                  onChange={({ train, validation, test }) =>
                    setExportForm((f) => ({
                      ...f,
                      train_ratio: train,
                      validation_ratio: validation,
                      test_ratio: test,
                    }))
                  }
                />
              </div>
              <div className="export-dataset-form-section">
                <HistoryContextControl
                  limited={exportForm.limit_history}
                  onLimitedChange={(v) => setExportForm((f) => ({ ...f, limit_history: v }))}
                  depthValue={numberInputDisplay(exportForm.history_depth, DEFAULT_HISTORY_DEPTH)}
                  onDepthValueChange={(value) =>
                    setExportForm((f) => ({
                      ...f,
                      history_depth: value,
                    }))
                  }
                  disabled={exportLoading}
                  depthInputId="datasets-export-history-depth"
                />
              </div>
              <div className="export-dataset-form-section">
                <div
                  className={`export-dataset-filters-card${exportModalValidation.fieldErrors[EXPORT_FILTERS_ID] ? ' export-dataset-filters-card--invalid' : ''}`}
                  id={EXPORT_FILTERS_ID}
                >
                  <div className="export-dataset-filters-label">
                    <i className="fas fa-filter"></i>
                    Signal filters
                  </div>
                  <p className="export-dataset-section-hint" style={{ marginTop: 0 }}>
                    Like, dislike, comment, and ground truth are enabled by default. At least one signal must stay
                    on — only matching messages are imported.
                  </p>
                  {exportModalValidation.fieldErrors[EXPORT_FILTERS_ID] ? (
                    <p className="modal-form-field__error" role="alert">
                      {exportModalValidation.fieldErrors[EXPORT_FILTERS_ID]}
                    </p>
                  ) : null}
                  <div className="export-dataset-selected-summary">
                    Selected:{' '}
                    {[
                      ...exportForm.reactions,
                      ...(exportForm.comment ? ['comment'] : []),
                      ...(exportForm.groundtruth ? ['ground truth'] : []),
                    ].length
                      ? [...exportForm.reactions, ...(exportForm.comment ? ['comment'] : []), ...(exportForm.groundtruth ? ['ground truth'] : [])].join(', ')
                      : 'none'}
                  </div>
                  <div className="export-dataset-checkboxes">
                    <label className="export-dataset-checkbox-item">
                      <input
                        type="checkbox"
                        checked={exportForm.reactions.includes('like')}
                        onChange={(e) =>
                          setExportForm((f) => {
                            const reactions: ('like' | 'dislike')[] = e.target.checked
                              ? [...f.reactions, 'like']
                              : f.reactions.filter((r) => r !== 'like');
                            const next = { ...f, reactions };
                            return exportFormHasAtLeastOneSignal(next) ? next : f;
                          })
                        }
                      />
                      <span>Like</span>
                    </label>
                    <label className="export-dataset-checkbox-item">
                      <input
                        type="checkbox"
                        checked={exportForm.reactions.includes('dislike')}
                        onChange={(e) =>
                          setExportForm((f) => {
                            const reactions: ('like' | 'dislike')[] = e.target.checked
                              ? [...f.reactions, 'dislike']
                              : f.reactions.filter((r) => r !== 'dislike');
                            const next = { ...f, reactions };
                            return exportFormHasAtLeastOneSignal(next) ? next : f;
                          })
                        }
                      />
                      <span>Dislike</span>
                    </label>
                    <label className="export-dataset-checkbox-item">
                      <input
                        type="checkbox"
                        checked={exportForm.comment}
                        onChange={(e) =>
                          setExportForm((f) => {
                            const next = { ...f, comment: e.target.checked };
                            return exportFormHasAtLeastOneSignal(next) ? next : f;
                          })
                        }
                      />
                      <span>Comment</span>
                    </label>
                    <label className="export-dataset-checkbox-item">
                      <input
                        type="checkbox"
                        checked={exportForm.groundtruth}
                        onChange={(e) =>
                          setExportForm((f) => {
                            const next = { ...f, groundtruth: e.target.checked };
                            return exportFormHasAtLeastOneSignal(next) ? next : f;
                          })
                        }
                      />
                      <span>Ground truth</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="export-dataset-form-section" id="datasets-export-reaction-weights-section">
                <FineTuningReactionWeightsFields
                  idPrefix={EXPORT_REACTION_WEIGHTS_ID_PREFIX}
                  value={exportForm.reactionWeights}
                  fieldErrors={stripFieldIdPrefix(exportModalValidation.fieldErrors, EXPORT_REACTION_WEIGHTS_PREFIX)}
                  onChange={(reactionWeights) => setExportForm((f) => ({ ...f, reactionWeights }))}
                  disabled={exportLoading}
                />
              </div>
              {exportPreviewData?.totals && (
                <div
                  className="export-dataset-form-section"
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid #C6E0F7',
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 13,
                    color: '#0072C6',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Preview (token totals)</div>
                  <p style={{ margin: '0 0 8px', fontSize: 12, opacity: 0.9 }}>
                    Estimate only. Final import may filter additional rows; a normal preview does not guarantee success.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {exportPreviewData.totals.dataset_tokens_raw_base != null && (
                      <span>
                        Raw (base):{' '}
                        <strong>{Number(exportPreviewData.totals.dataset_tokens_raw_base).toLocaleString()}</strong>
                      </span>
                    )}
                    {exportPreviewData.totals.dataset_tokens_raw != null && (
                      <span>
                        Raw: <strong>{Number(exportPreviewData.totals.dataset_tokens_raw).toLocaleString()}</strong>
                      </span>
                    )}
                    {exportPreviewData.totals.dataset_tokens_prepared != null && (
                      <span>
                        Prepared:{' '}
                        <strong>{Number(exportPreviewData.totals.dataset_tokens_prepared).toLocaleString()}</strong>
                      </span>
                    )}
                  </div>
                </div>
              )}
              {exportPreviewData?.warning ? (
                <DatasetWarningBanner warning={exportPreviewData.warning} />
              ) : null}
            </div>
            <div className="export-dataset-modal-footer">
              <div className="export-dataset-modal-footer-actions">
                <button type="button" className="btn-secondary" onClick={closeExportModal}>
                Cancel
              </button>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => void handleExportPreview()}
                  disabled={
                    exportLoading ||
                    exportPreviewLoading ||
                    exportSelectAllDialogsBusy ||
                    (exportSourceDialogsLoading && exportSelectedDialogIds.length === 0) ||
                    !exportHasAtLeastOneSignal ||
                    !exportPeriodParams ||
                    exportSelectedUserIds.length === 0 ||
                    exportSelectedDialogIds.length === 0
                  }
                >
                  {exportPreviewLoading
                    ? 'Previewing…'
                    : exportSelectAllDialogsBusy
                      ? 'Selecting dialogs…'
                      : 'Preview'}
                </button>
                <button
                  type="button"
                  className="export-dataset-btn-submit"
                  onClick={() => void handleExportSubmit()}
                  disabled={
                    exportLoading ||
                    exportPreviewLoading ||
                    exportSelectAllDialogsBusy ||
                    !exportForm.dataset_name.trim() ||
                    !exportHasAtLeastOneSignal ||
                    !exportPeriodParams ||
                    exportSelectedUserIds.length === 0 ||
                    exportSelectedDialogIds.length === 0
                  }
                >
                  {exportLoading ? 'Importing...' : 'Import dataset'}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>,
        embedded
      )}

      {/* Start Experiment Modal */}
      {startExpOpen &&
        renderEmbeddedOverlay(
        <div className="modal-overlay">
          <div className="modal-content modal-content--start-dataset">
            <div className="modal-header">
              <h3>Start New Dataset</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setStartExpOpen(false);
                  startExpModalValidation.clearErrors();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body" ref={startExpModalBodyRef}>
              {startExpModalValidation.bannerError && (
                <div
                  id={START_EXP_ERROR_BANNER_ID}
                  className="modal-alert-error modal-alert-error--sticky"
                  role="alert"
                >
                  <div className="admin-message error" style={{ margin: 0 }}>
                    <i className="fas fa-exclamation-circle"></i> {startExpModalValidation.bannerError}
                  </div>
                </div>
              )}
              <ModalFormField
                id={START_EXP_DATASET_NAME_ID}
                label="Dataset name"
                required
                error={startExpModalValidation.fieldErrors[START_EXP_DATASET_NAME_ID]}
                hint="This name is used for the dataset when the run completes."
                className="form-group"
              >
                <input
                  id={START_EXP_DATASET_NAME_ID}
                  type="text"
                  value={startExp.experiment_name}
                  onChange={(e) => setStartExp((p) => ({ ...p, experiment_name: e.target.value }))}
                  placeholder="Enter dataset name"
                  disabled={loading}
                  style={{ width: '100%' }}
                  aria-invalid={Boolean(startExpModalValidation.fieldErrors[START_EXP_DATASET_NAME_ID])}
                />
              </ModalFormField>
              <div className="form-group">
                <label>Virtual users &amp; personas</label>
                <div className="exp-persona-mode-toggle" role="group" aria-label="Persona assignment mode">
                  <button
                    type="button"
                    className={`exp-persona-mode-toggle__btn${!startExp.useCustomPersonaPairs ? ' exp-persona-mode-toggle__btn--active' : ''}`}
                    disabled={loading}
                    onClick={() => setStartExp((p) => ({ ...p, useCustomPersonaPairs: false }))}
                  >
                    Default personas
                  </button>
                  <button
                    type="button"
                    className={`exp-persona-mode-toggle__btn${startExp.useCustomPersonaPairs ? ' exp-persona-mode-toggle__btn--active' : ''}`}
                    disabled={loading}
                    onClick={() => setStartExp((p) => ({ ...p, useCustomPersonaPairs: true }))}
                  >
                    Custom pairs
                  </button>
                </div>
                <div className="form-hint-text" style={{ marginTop: 6, fontSize: '12px', color: '#5B7893' }}>
                  {startExp.useCustomPersonaPairs
                    ? 'Add virtual user + persona pairs. One virtual user can have several different personas, but the same persona cannot be assigned twice.'
                    : 'Select virtual users and admins; each user uses their default assigned persona.'}
              </div>
              </div>

              {startExp.useCustomPersonaPairs ? (
                <>
                  <ModalFormField
                    id={START_EXP_USER_PERSONA_PAIRS_ID}
                    error={startExpModalValidation.fieldErrors[START_EXP_USER_PERSONA_PAIRS_ID]}
                    className="form-group"
                  >
                    <ExperimentPersonaPairsEditor
                      title="Virtual Users + Personas"
                      required
                      virtualUserType="user"
                      personaType="virtual_user"
                      pairs={startExp.userPersonaPairs}
                      onChange={(userPersonaPairs) => setStartExp((p) => ({ ...p, userPersonaPairs }))}
                      virtualUsers={virtualUsers}
                      integrationId={integrationId}
                      disabled={loading}
                    />
                  </ModalFormField>
              <div className="form-group">
                    <ModalFormField
                      id={START_EXP_ADMIN_PERSONA_PAIRS_ID}
                      error={startExpModalValidation.fieldErrors[START_EXP_ADMIN_PERSONA_PAIRS_ID]}
                    >
                    <ExperimentPersonaPairsEditor
                      title="Virtual Admins + Personas"
                      virtualUserType="admin"
                      personaType="virtual_admin"
                      pairs={startExp.adminPersonaPairs}
                      onChange={(adminPersonaPairs) => setStartExp((p) => ({ ...p, adminPersonaPairs }))}
                      virtualUsers={virtualUsers}
                      integrationId={integrationId}
                      disabled={loading}
                    />
                    </ModalFormField>
                  </div>
                </>
              ) : (
                <>
              <ModalFormField
                id={START_EXP_VIRTUAL_USERS_ID}
                label="Virtual Users"
                required
                error={startExpModalValidation.fieldErrors[START_EXP_VIRTUAL_USERS_ID]}
                className="form-group"
              >
                {virtualUsers.filter((u) => u.type === 'user').length === 0 ? (
                  <div className="admin-message error" style={{ margin: 0 }}>
                    <i className="fas fa-exclamation-circle"></i> No virtual users available. Create at least one virtual user first.
                  </div>
                ) : (
                  <MultiSelect
                    options={virtualUsers
                      .filter((u) => u.type === 'user')
                      .map((u) => ({
                        value: u.id,
                        label: `${u.name} · ${formatVirtualUserCreatedAt(u.created_at)} (${u.id.substring(0, 8)}...)`,
                      }))}
                    value={startExp.virtual_user_ids}
                    onChange={(selected) => setStartExp((p) => ({ ...p, virtual_user_ids: selected }))}
                    placeholder="Select virtual users..."
                    disabled={loading}
                  />
                )}
              </ModalFormField>
              <div className="form-group">
                <label>Virtual Admins (optional)</label>
                {virtualUsers.filter((u) => u.type === 'admin').length === 0 ? (
                  <div className="form-hint-text" style={{ marginTop: 0 }}>
                    No virtual admins available. This field is optional.
                  </div>
                ) : (
                  <MultiSelect
                    options={virtualUsers
                      .filter((u) => u.type === 'admin')
                      .map((u) => ({
                        value: u.id,
                        label: `${u.name} · ${formatVirtualUserCreatedAt(u.created_at)} (${u.id.substring(0, 8)}...)`,
                      }))}
                    value={startExp.virtual_admin_ids}
                    onChange={(selected) => setStartExp((p) => ({ ...p, virtual_admin_ids: selected }))}
                    placeholder="Select virtual admins (optional)..."
                    disabled={loading}
                  />
                )}
              </div>
                </>
              )}
              <ModalFormField
                id={START_EXP_FIRST_MESSAGE_ID}
                label="First message from assistant"
                error={startExpModalValidation.fieldErrors[START_EXP_FIRST_MESSAGE_ID]}
                hint="This message starts every generated dialog and is shown as the assistant's first reply."
                className="form-group"
              >
                <textarea
                  id={START_EXP_FIRST_MESSAGE_ID}
                  value={startExp.first_message}
                  onChange={(e) => setStartExp((p) => ({ ...p, first_message: e.target.value }))}
                  placeholder={DEFAULT_FIRST_MESSAGE}
                  disabled={loading}
                  rows={3}
                  style={{ width: '100%', resize: 'vertical' }}
                  aria-invalid={Boolean(startExpModalValidation.fieldErrors[START_EXP_FIRST_MESSAGE_ID])}
                />
              </ModalFormField>
              <ModalFormField
                id={START_EXP_DIALOG_COUNT_ID}
                label="Dialog count"
                error={startExpModalValidation.fieldErrors[START_EXP_DIALOG_COUNT_ID]}
                hint={`Max: ${MAX_EXPERIMENT_DIALOGS}`}
                className="form-group"
              >
                <input
                  id={START_EXP_DIALOG_COUNT_ID}
                  type="number"
                  min={DATASET_GENERATION_MIN_DIALOGS}
                  max={MAX_EXPERIMENT_DIALOGS}
                  value={startExp.dialog_count}
                  onChange={(e) =>
                    setStartExp((p) => ({
                      ...p,
                      dialog_count: sanitizePositiveIntegerDraft(
                        e.target.value,
                        DATASET_GENERATION_MIN_DIALOGS,
                        MAX_EXPERIMENT_DIALOGS
                      ),
                    }))
                  }
                  onBlur={(e) =>
                    setStartExp((p) => ({
                      ...p,
                      dialog_count: commitPositiveIntegerDraft(
                        e.target.value,
                        DATASET_GENERATION_MIN_DIALOGS,
                        MAX_EXPERIMENT_DIALOGS
                      ),
                    }))
                  }
                  aria-invalid={Boolean(startExpModalValidation.fieldErrors[START_EXP_DIALOG_COUNT_ID])}
                />
              </ModalFormField>
              <ModalFormField
                id={START_EXP_MESSAGES_PER_DIALOG_ID}
                label="Messages per dialog"
                error={startExpModalValidation.fieldErrors[START_EXP_MESSAGES_PER_DIALOG_ID]}
                hint={`Max: ${MAX_MESSAGES_PER_DIALOG}`}
                className="form-group"
              >
                <input
                  id={START_EXP_MESSAGES_PER_DIALOG_ID}
                  type="number"
                  min={DATASET_GENERATION_MIN_MESSAGES_PER_DIALOG}
                  max={MAX_MESSAGES_PER_DIALOG}
                  value={startExp.messages_per_dialog}
                  onChange={(e) =>
                    setStartExp((p) => ({
                      ...p,
                      messages_per_dialog: sanitizePositiveIntegerDraft(
                        e.target.value,
                        DATASET_GENERATION_MIN_MESSAGES_PER_DIALOG,
                        MAX_MESSAGES_PER_DIALOG
                      ),
                    }))
                  }
                  onBlur={(e) =>
                    setStartExp((p) => ({
                      ...p,
                      messages_per_dialog: commitPositiveIntegerDraft(
                        e.target.value,
                        DATASET_GENERATION_MIN_MESSAGES_PER_DIALOG,
                        MAX_MESSAGES_PER_DIALOG
                      ),
                    }))
                  }
                  aria-invalid={Boolean(startExpModalValidation.fieldErrors[START_EXP_MESSAGES_PER_DIALOG_ID])}
                />
              </ModalFormField>

              {/* Стартовый стейт: прогон садится сразу на нужный этап вместо того,
                  чтобы идти к нему через весь граф и платить за каждый ход. */}
              {workflowStatesStatus === 'ready' && workflowStates && workflowStates.states.length > 0 ? (
                <div className="form-group">
                  <label htmlFor="start-exp-entry-state">Start from state</label>
                  <select
                    id="start-exp-entry-state"
                    className="form-control"
                    value={startExp.entry_state_id || workflowStates.entry_state_id}
                    onChange={(e) => setStartExp((prev) => ({ ...prev, entry_state_id: e.target.value }))}
                  >
                    {workflowStates.states.map((state) => {
                      const flags = state.coordinator_flags_required.length;
                      return (
                        <option key={state.state_id} value={state.state_id}>
                          {state.state_id}
                          {state.state_id === workflowStates.entry_state_id ? ' — entry' : ''}
                          {flags > 0 ? ` — needs ${flags} flag${flags === 1 ? '' : 's'}` : ''}
                        </option>
                      );
                    })}
                  </select>
                  {(() => {
                    const chosenId = startExp.entry_state_id || workflowStates.entry_state_id;
                    const chosen = workflowStates.states.find((state) => state.state_id === chosenId);
                    const required = chosen?.coordinator_flags_required ?? [];
                    if (required.length === 0) {
                      return (
                        <span className="entry-state-hint">
                          Workflow v{workflowStates.version}. Leave the entry state to start where the graph starts.
                        </span>
                      );
                    }
                    // Флаги предыдущих этапов бэкенд намеренно не подделывает,
                    // поэтому диалог выйдет из такого стейта на первом же ходу.
                    return (
                      <div className="dataset-warning-banner dataset-warning-banner--compact">
                        <i className="fas fa-exclamation-triangle" aria-hidden />
                        <div>
                          <strong>This state expects flags from earlier stages</strong>
                          <span>
                            Needs {required.join(', ')}. Starting here means the dialogue leaves the state on its
                            first turn — the run will be short and will not show the stage itself.
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : null}
              {workflowStatesStatus === 'error' ? (
                <div className="form-group">
                  <div className="dataset-warning-banner dataset-warning-banner--compact">
                    <i className="fas fa-exclamation-triangle" aria-hidden />
                    <div>
                      <strong>Could not load the workflow states</strong>
                      <span>
                        The run will start from the entry state.{' '}
                        <button type="button" className="entry-state-retry" onClick={() => void loadWorkflowStates()}>
                          Retry
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="form-group dataset-gen-token-estimate">
                <div className="dataset-gen-token-estimate__head">
                  <h4 className="dataset-gen-token-estimate__title">
                    <i className="fas fa-coins" aria-hidden />
                    Estimated token usage
                  </h4>
                  {tokenPreviewLoading ? (
                    <span className="dataset-gen-token-estimate__status">
                      <i className="fas fa-spinner fa-spin" aria-hidden /> Calculating…
                    </span>
                  ) : null}
                </div>
                {!assistantConfigEnvelope ? (
                  <p className="dataset-gen-token-estimate__status">
                    Load integration assistant config to estimate tokens.
                  </p>
                ) : tokenPreviewError ? (
                  <p className="dataset-gen-token-estimate__error" role="alert">
                    {tokenPreviewError}
                  </p>
                ) : tokenPreviewResult ? (
                  <DatasetGenerationTokenPreviewView data={tokenPreviewResult} />
                ) : (
                  <p className="dataset-gen-token-estimate__status">
                    Adjust dialog volume to see an estimate.
                  </p>
                )}
              </div>
              <div className="form-group">
                <HistoryContextControl
                  limited={startExp.limit_history}
                  onLimitedChange={(v) => setStartExp((p) => ({ ...p, limit_history: v }))}
                  depthValue={startExp.history_depth}
                  onDepthValueChange={(value) => setStartExp((p) => ({ ...p, history_depth: value }))}
                  disabled={loading}
                  depthInputId="datasets-start-exp-history-depth"
                />
              </div>
              <div className="form-group">
                <FineTuningReactionWeightsFields
                  idPrefix={START_EXP_REACTION_WEIGHTS_PREFIX}
                  value={startExp.reactionWeights}
                  fieldErrors={stripFieldIdPrefix(startExpModalValidation.fieldErrors, START_EXP_REACTION_WEIGHTS_PREFIX)}
                  onChange={(reactionWeights) => setStartExp((p) => ({ ...p, reactionWeights }))}
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Train / Validation ratio</label>
                <div
                  className="train-val-ratio"
                  style={
                    {
                      '--train-pct': `${startExp.train_ratio}%`,
                    } as React.CSSProperties
                  }
                >
                  <div className="train-val-ratio__stats" role="group" aria-label="Train and validation split">
                    <div className="train-val-ratio__stat train-val-ratio__stat--train">
                      <span className="train-val-ratio__stat-label">Train</span>
                      <span className="train-val-ratio__stat-value">{startExp.train_ratio}%</span>
                    </div>
                    <div className="train-val-ratio__stat train-val-ratio__stat--val">
                      <span className="train-val-ratio__stat-label">Validation</span>
                      <span className="train-val-ratio__stat-value">{startExp.validation_ratio}%</span>
                    </div>
                  </div>
                  <div className="train-val-ratio__slider-shell">
                    <input
                      type="range"
                      className="train-val-ratio__range"
                      min={0}
                      max={100}
                      step={1}
                      value={startExp.train_ratio}
                      onChange={(e) => {
                        const trainValue = Number(e.target.value);
                        setStartExp((p) => ({
                          ...p,
                          train_ratio: trainValue,
                          validation_ratio: 100 - trainValue,
                        }));
                      }}
                      disabled={loading}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={startExp.train_ratio}
                      aria-valuetext={`${startExp.train_ratio}% train, ${startExp.validation_ratio}% validation`}
                      aria-label="Adjust train versus validation percentage"
                    />
                  </div>
                  <p className="train-val-ratio__hint">
                    One slider sets both sides — train and validation always add up to 100%.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setStartExpOpen(false);
                  startExpModalValidation.clearErrors();
                }}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={() => void handleStartExperiment()}
                disabled={startExperimentDisabled}
                title={
                  startExp.useCustomPersonaPairs
                    ? startExp.userPersonaPairs.length === 0
                      ? 'Add at least one virtual user + persona pair'
                      : undefined
                    : virtualUsersMissingPersona.length > 0
                    ? 'Each selected virtual user must have a persona assigned'
                    : undefined
                }
              >
                Start
              </button>
            </div>
          </div>
        </div>,
        embedded
      )}
    </div>
  );
};

export default DatasetsPage;
